class KeilrahmenConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) {
      this.addResumeListeners();
      this.addCustomEntryListener();
      this.addSizeRequestListeners();
      this.handleResume();
      return;
    }

    this.isInitialized = true;
    this.config = this.readConfig();
    this.locale = this.normalizeLocale(this.config.locale);
    this.i18n = this.buildI18n();
    this.mobileLayoutVariant = this.normalizeMobileLayoutVariant(this.config.mobileLayoutVariant);
    this.mobileAutoJumpDelay = this.clamp(this.readNumber(this.config.mobileAutoJumpDelay, 4000), 1000, 10000);
    this.desktopIntroEnabled = this.config.desktopIntro?.enabled !== false;
    this.desktopIntroDelay = this.clamp(this.readNumber(this.config.desktopIntro?.delay, 1200), 1000, 2000);
    this.classList.add(`fa-kc--mobile-${this.mobileLayoutVariant}`);
    this.productDescriptionTemplates = { ...(this.config.productDescriptions || {}) };
    this.items = this.prepareItems(this.config.items || []);
    this.supportOverrides = this.config.backSupportOverrides || {};
    this.lengthNumber = this.querySelector('[data-field="length-number"]');
    this.widthNumber = this.querySelector('[data-field="width-number"]');
    this.lengthRange = this.querySelector('[data-field="length-range"]');
    this.widthRange = this.querySelector('[data-field="width-range"]');
    this.lengthCard = this.querySelector('[data-field-card="length"]');
    this.widthCard = this.querySelector('[data-field-card="width"]');
    this.lengthTitle = this.querySelector('[data-field-title="length"]');
    this.widthTitle = this.querySelector('[data-field-title="width"]');
    this.stepperButtons = [...this.querySelectorAll('[data-stepper]')];
    this.lengthTicks = this.querySelector('[data-ticks="length"]');
    this.widthTicks = this.querySelector('[data-ticks="width"]');
    this.thicknessFieldset = this.querySelector('[data-fieldset="thickness"]');
    this.thicknessInputs = [...this.querySelectorAll('[data-field="thickness"]')];
    this.includeCustomInput = this.querySelector('[data-field="include-custom"]');
    this.customToggle = this.includeCustomInput?.closest('.fa-kc__custom-toggle') || null;
    this.result = this.querySelector('[data-result]');
    this.quickSizes = this.querySelector('[data-quick-sizes]');
    this.inputIntro = this.querySelector('[data-input-intro]');
    this.mobileDock = null;
    this.mobileRevealTimer = null;
    this.mobileAlignTimer = null;
    this.mobileDockTimer = null;
    this.mobileDockLoadingTimer = null;
    this.mobileResultOpened = false;
    this.desktopIntroTimer = null;
    this.desktopIntroRevealed = false;
    this.mobileViewportHandler = null;
    this.hasUserInput = false;
    this.lastCustomQuote = null;
    this.customQuoteRequestId = 0;
    this.deliveryRequestIds = {};
    this.quantities = {};
    this.inputIntroTimeout = null;
    this.inputIntroHideTimeout = null;
    this.inputIntroLoopTimers = [];

    this.state = {
      length: this.readNumber(this.lengthNumber?.value, 40),
      width: this.readNumber(this.widthNumber?.value, 50),
      thicknessCm: 2,
      frameType: 'studio_2cm',
      includeCustom: false,
    };
    this.hasSharedUrlState = this.applySharedUrlState();
    if (this.hasSharedUrlState) {
      this.hasUserInput = true;
      this.mobileResultOpened = true;
      this.classList.add('fa-kc--has-user-input', 'fa-kc--result-open');
    }

    ['input', 'change', 'pointerup', 'touchend'].forEach((eventName) => {
      this.lengthRange?.addEventListener(eventName, () => this.handleRangeInput('length'));
      this.widthRange?.addEventListener(eventName, () => this.handleRangeInput('width'));
    });
    this.lengthNumber?.addEventListener('input', (event) => this.handleNumberInput('length', event));
    this.widthNumber?.addEventListener('input', (event) => this.handleNumberInput('width', event));
    this.lengthNumber?.addEventListener('change', (event) => this.handleNumberInput('length', event, true));
    this.widthNumber?.addEventListener('change', (event) => this.handleNumberInput('width', event, true));
    this.stepperButtons.forEach((button) => button.addEventListener('click', () => this.handleStepperClick(button)));
    this.thicknessInputs.forEach((input) => input.addEventListener('change', () => this.handleThicknessChange(input)));
    this.includeCustomInput?.addEventListener('change', () => this.handleCustomToggle());
    this.addCustomEntryListener();
    this.addSizeRequestListeners();
    this.result?.addEventListener('click', (event) => this.handleResultClick(event));
    this.result?.addEventListener('change', (event) => this.handleQuantityChange(event));
    this.result?.addEventListener('submit', (event) => this.handleNotifySubmit(event));
    this.quickSizes?.addEventListener('click', (event) => this.handleQuickSizeClick(event));
    this.createMobileDock();
    this.setupDesktopIntro();
    this.mobileScrollHandler = () => this.handleMobileScroll();
    this.mobileViewportHandler = () => this.updateMobileViewportMetrics();
    window.addEventListener('scroll', this.mobileScrollHandler, { passive: true });
    window.addEventListener('resize', this.mobileViewportHandler, { passive: true });
    window.visualViewport?.addEventListener('resize', this.mobileViewportHandler, { passive: true });
    window.visualViewport?.addEventListener('scroll', this.mobileViewportHandler, { passive: true });
    this.addInputIntroDismissListeners();
    this.resumeHandler = () => this.handleResume();
    this.addResumeListeners();

    this.applyModeConstraints();
    this.renderQuickSizes();
    this.render();
    this.updateMobileDock();
    this.updateMobileViewportMetrics();
    if (new URLSearchParams(window.location.search).get('kc_request') === '1') {
      this.querySelector('[data-size-request-open]')?.click();
    }
    this.startInputIntro();
    this.loadSupportMap();
    this.loadProductDescriptions();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.sizeRequestClickHandler);
    this.removeEventListener('submit', this.sizeRequestSubmitHandler);
    this.sizeRequestClickHandler = null;
    this.sizeRequestSubmitHandler = null;
    this.removeResumeListeners();
    this.removeCustomEntryListener();
    this.clearInputIntroTimers();
    window.clearTimeout(this.mobileRevealTimer);
    window.clearTimeout(this.mobileAlignTimer);
    window.clearTimeout(this.mobileDockTimer);
    window.clearTimeout(this.mobileDockLoadingTimer);
    window.clearTimeout(this.desktopIntroTimer);
    window.removeEventListener('scroll', this.mobileScrollHandler);
    if (this.mobileViewportHandler) {
      window.removeEventListener('resize', this.mobileViewportHandler);
      window.visualViewport?.removeEventListener('resize', this.mobileViewportHandler);
      window.visualViewport?.removeEventListener('scroll', this.mobileViewportHandler);
    }
    this.closeImageModal();
  }

  addCustomEntryListener() {
    this.removeCustomEntryListener();
    this.customEntryRoot = this.closest('.fa-kc');
    this.customEntryHandler ||= (event) => this.handleCustomEntryClick(event);
    this.customEntryRoot?.addEventListener('click', this.customEntryHandler);
  }

  removeCustomEntryListener() {
    this.customEntryRoot?.removeEventListener('click', this.customEntryHandler);
    this.customEntryRoot = null;
  }

  handleCustomEntryClick(event) {
    const link = event.target.closest?.('[data-fa-include-custom]');
    if (!link || link.closest('.fa-kc') !== this.customEntryRoot || event.defaultPrevented
      || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
      || !this.includeCustomInput) return;

    // Round canvases have no custom-size mode. Keep 4 cm depth when switching to a rectangle.
    const rectangularInput = this.isRoundMode()
      ? this.thicknessInputs.find((input) => input.value === '4' && !input.disabled)
      : null;
    if (this.includeCustomInput.disabled && !rectangularInput) return;

    event.preventDefault();
    rectangularInput?.click();
    // Use the existing checkbox change flow; repeated entry clicks must never switch it off.
    if (!this.includeCustomInput.checked) this.includeCustomInput.click();
    this.hideInputIntro(true);
    this.includeCustomInput.focus({ preventScroll: true });
    const panel = this.querySelector('.fa-kc__panel--input');
    if (panel) {
      window.clearTimeout(this.mobileAlignTimer);
      const top = panel.getBoundingClientRect().top + window.scrollY - this.mobileStickyOffset() - 16;
      const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth';
      window.scrollTo({ top: Math.max(0, top), behavior });
    }
  }

  isDesktopIntroViewport() {
    return window.matchMedia?.('(min-width: 901px)')?.matches || window.innerWidth >= 901;
  }

  setupDesktopIntro() {
    if (this.hasSharedUrlState || !this.desktopIntroEnabled || !this.isDesktopIntroViewport()) {
      this.classList.remove('fa-kc--desktop-start', 'fa-kc--desktop-loading');
      this.classList.add('fa-kc--desktop-revealed');
      this.desktopIntroRevealed = true;
      return;
    }

    this.classList.add('fa-kc--desktop-start');
  }

  scheduleDesktopIntroReveal() {
    if (!this.desktopIntroEnabled || this.desktopIntroRevealed || !this.isDesktopIntroViewport()) return;

    window.clearTimeout(this.desktopIntroTimer);
    this.alignDesktopIntroPanel();
    this.classList.remove('fa-kc--desktop-start');
    this.classList.add('fa-kc--desktop-loading');

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const delay = reduceMotion ? Math.min(this.desktopIntroDelay, 800) : this.desktopIntroDelay;
    this.desktopIntroTimer = window.setTimeout(() => {
      if (!this.isConnected) return;
      this.desktopIntroRevealed = true;
      this.classList.remove('fa-kc--desktop-loading');
      this.classList.add('fa-kc--desktop-revealed');
    }, delay);
  }

  alignDesktopIntroPanel() {
    if (!this.isDesktopIntroViewport() || this.desktopIntroRevealed) return;

    const target = this.querySelector('.fa-kc__panel--input')
      || this.querySelector('.fa-kc__shell')
      || this;
    const top = target.getBoundingClientRect().top + window.scrollY - this.mobileStickyOffset() - 8;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  addInputIntroDismissListeners() {
    const controls = [
      this.lengthNumber,
      this.widthNumber,
      this.lengthRange,
      this.widthRange,
      this.includeCustomInput,
      ...this.stepperButtons,
      ...this.thicknessInputs,
    ].filter(Boolean);

    controls.forEach((control) => {
      ['pointerdown', 'focus', 'input', 'change'].forEach((eventName) => {
        control.addEventListener(eventName, () => this.hideInputIntro(true), { once: eventName === 'pointerdown' });
      });
    });
  }

  startInputIntro() {
    if (this.hasSharedUrlState) return;
    const introConfig = this.config.inputIntro || {};
    if (!introConfig.enabled || !this.inputIntro) return;

    const targets = this.inputIntroTargets();
    if (!targets.length) return;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const effect = reduceMotion ? 'subtle' : this.inputIntroEffect(introConfig.effect);
    const duration = this.clamp(this.readNumber(introConfig.duration, 5000), 3000, 60000);

    this.clearInputIntroTimers();
    if (effect === 'guided') {
      this.applyGuidedInputIntro();
      this.startGuidedInputIntroLoop({
        pulseSpeed: this.clamp(this.readNumber(introConfig.pulseSpeed, 2000), 600, 5000),
        pause: this.clamp(this.readNumber(introConfig.pause, 1000), 0, 5000),
      });
    } else {
      targets.forEach((target) => target.classList.add('fa-kc__intro-target', `fa-kc__intro-target--${effect}`));
    }
    this.inputIntro.hidden = false;
    this.inputIntro.className = `fa-kc__input-intro fa-kc__input-intro--${effect} is-visible`;
    this.inputIntroTimeout = window.setTimeout(() => this.hideInputIntro(), duration);
  }

  hideInputIntro(immediate = false) {
    if (!this.inputIntro || this.inputIntro.hidden) return;

    this.clearInputIntroTimers();
    [...this.querySelectorAll('.fa-kc__intro-target')].forEach((target) => {
      target.classList.remove(
        'fa-kc__intro-target',
        'fa-kc__intro-target--spotlight',
        'fa-kc__intro-target--pulse',
        'fa-kc__intro-target--swipe',
        'fa-kc__intro-target--subtle',
        'fa-kc__intro-target--guided',
        'fa-kc__intro-guided-range',
        'fa-kc__intro-guided-input',
        'fa-kc__intro-guided-thickness',
        'is-guided-active',
      );
    });

    if (immediate) {
      this.inputIntro.hidden = true;
      this.inputIntro.className = 'fa-kc__input-intro';
      return;
    }

    this.inputIntro.classList.add('is-hiding');
    this.inputIntroHideTimeout = window.setTimeout(() => {
      if (!this.inputIntro) return;
      this.inputIntro.hidden = true;
      this.inputIntro.className = 'fa-kc__input-intro';
    }, 220);
  }

  clearInputIntroTimers() {
    window.clearTimeout(this.inputIntroTimeout);
    window.clearTimeout(this.inputIntroHideTimeout);
    this.clearGuidedInputIntroLoop();
    this.inputIntroTimeout = null;
    this.inputIntroHideTimeout = null;
  }

  clearGuidedInputIntroLoop() {
    this.inputIntroLoopTimers.forEach((timer) => window.clearTimeout(timer));
    this.inputIntroLoopTimers = [];
    this.querySelectorAll('.is-guided-active').forEach((target) => target.classList.remove('is-guided-active'));
  }

  inputIntroTargets() {
    return [
      this.lengthNumber?.closest('.fa-kc__input-card'),
      this.widthNumber?.closest('.fa-kc__input-card'),
      this.thicknessFieldset,
    ].filter((target) => target instanceof HTMLElement);
  }

  applyGuidedInputIntro() {
    [this.lengthNumber, this.widthNumber]
      .map((input) => input?.closest('.fa-kc__input-card'))
      .filter((target) => target instanceof HTMLElement)
      .forEach((target) => target.classList.add('fa-kc__intro-target', 'fa-kc__intro-target--guided', 'fa-kc__intro-guided-range'));

    if (this.thicknessFieldset instanceof HTMLElement) {
      this.thicknessFieldset.classList.add('fa-kc__intro-target', 'fa-kc__intro-target--guided', 'fa-kc__intro-guided-thickness');
    }
  }

  startGuidedInputIntroLoop({ pulseSpeed, pause }) {
    const groups = [
      [
        this.lengthNumber?.closest('.fa-kc__input-card'),
        this.widthNumber?.closest('.fa-kc__input-card'),
      ],
      [this.thicknessFieldset],
    ].map((group) => group.filter((target) => target instanceof HTMLElement));

    if (!groups.some((group) => group.length > 0)) return;

    this.inputIntroTargets().forEach((target) => target.style.setProperty('--fa-kc-guided-pulse-speed', `${pulseSpeed}ms`));
    if (this.thicknessFieldset instanceof HTMLElement) {
      this.thicknessFieldset.style.setProperty('--fa-kc-guided-pulse-speed', `${pulseSpeed}ms`);
    }

    const runStep = (index = 0) => {
      this.querySelectorAll('.is-guided-active').forEach((target) => target.classList.remove('is-guided-active'));
      groups[index].forEach((target) => target.classList.add('is-guided-active'));
      const nextIndex = (index + 1) % groups.length;
      const timer = window.setTimeout(() => runStep(nextIndex), pulseSpeed + pause);
      this.inputIntroLoopTimers.push(timer);
    };

    runStep(0);
  }

  inputIntroEffect(effect) {
    return ['spotlight', 'pulse', 'swipe', 'guided', 'subtle'].includes(effect) ? effect : 'spotlight';
  }

  normalizeMobileLayoutVariant(value) {
    return ['compact_jump', 'result_dock', 'step_flow'].includes(value) ? value : 'compact_jump';
  }

  isMobileViewport() {
    return window.matchMedia?.('(max-width: 640px)')?.matches || window.innerWidth <= 640;
  }

  mobileViewportHeight() {
    return Math.max(320, Math.round(window.visualViewport?.height || window.innerHeight || 640));
  }

  mobileStickyOffset() {
    const candidates = [
      document.querySelector('sticky-header'),
      document.querySelector('header'),
      document.querySelector('.shopify-section-group-header-group'),
    ].filter((element) => element instanceof HTMLElement);

    return candidates.reduce((offset, element) => {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      const isTopBar = rect.top <= 1 && rect.bottom > 0 && ['fixed', 'sticky'].includes(style.position);
      return isTopBar ? Math.max(offset, Math.min(rect.bottom, 140)) : offset;
    }, Math.max(0, Math.round(window.visualViewport?.offsetTop || 0)));
  }

  updateMobileViewportMetrics() {
    if (this.mobileLayoutVariant !== 'result_dock') return;

    const safeHeight = Math.max(320, this.mobileViewportHeight() - this.mobileStickyOffset());
    const guardSpace = safeHeight + 140;
    this.style.setProperty('--fa-kc-mobile-safe-height', `${safeHeight}px`);
    this.style.setProperty('--fa-kc-result-guard-space', `${guardSpace}px`);
  }

  createMobileDock() {
    if (this.mobileLayoutVariant !== 'result_dock') return;

    this.mobileDock = document.createElement('div');
    this.mobileDock.className = 'fa-kc__mobile-dock';
    this.mobileDock.setAttribute('aria-live', 'polite');
    this.mobileDock.innerHTML = `<button type="button" data-mobile-result-link>${this.escapeHtml(this.t('resultView'))}</button>`;
    this.mobileDock.addEventListener('click', (event) => {
      if (event.target.closest('[data-mobile-result-link]')) {
        event.preventDefault();
        this.mobileResultOpened = true;
        this.updateMobileViewportMetrics();
        this.classList.add('fa-kc--result-open');
        this.classList.remove('fa-kc--show-result-dock', 'fa-kc--show-result-loading');
        window.clearTimeout(this.mobileDockTimer);
        window.clearTimeout(this.mobileDockLoadingTimer);
        this.scrollToResult('smooth', true);
      }
    });
    this.append(this.mobileDock);
  }

  scheduleMobileResultReveal(mode = 'auto') {
    if (!this.isMobileViewport()) return;
    if (!['compact_jump', 'step_flow'].includes(this.mobileLayoutVariant)) return;
    if (!this.state.length || !this.state.width || !this.state.thicknessCm) return;

    window.clearTimeout(this.mobileRevealTimer);
    this.mobileRevealTimer = window.setTimeout(() => {
      if (!this.isConnected || !this.isMobileViewport()) return;
      this.scrollToResult('smooth');
    }, this.mobileAutoJumpDelay);
  }

  scrollToResult(behavior = 'smooth', alignTop = false) {
    if (!this.result) return;
    const resultTarget = alignTop
      ? this.result
      : this.mobileLayoutVariant === 'step_flow'
      ? this.result
      : this.result.querySelector('.fa-kc__matches, .fa-kc__custom-order, .fa-kc__empty--request') || this.result;

    resultTarget.scrollIntoView({
      behavior,
      block: alignTop || this.mobileLayoutVariant === 'step_flow' ? 'start' : 'nearest',
    });
  }

  updateMobileDock() {
    if (!this.mobileDock) return;
    this.mobileDock.innerHTML = `
      <div class="fa-kc__mobile-dock-loading" aria-hidden="true">
        <span class="fa-kc__mobile-spinner"></span>
        <span>${this.escapeHtml(this.t('resultLoading'))}</span>
      </div>
      <button type="button" data-mobile-result-link>${this.escapeHtml(this.t('resultView'))}</button>
    `;
  }

  scheduleMobileDockButton() {
    if (this.mobileLayoutVariant !== 'result_dock') return;
    this.updateMobileViewportMetrics();
    window.clearTimeout(this.mobileDockTimer);
    window.clearTimeout(this.mobileDockLoadingTimer);
    this.classList.remove('fa-kc--show-result-dock', 'fa-kc--show-result-loading');
    if (!this.hasUserInput || this.mobileResultOpened || !this.isMobileViewport()) return;

    const dockDelay = this.mobileAutoJumpDelay;
    const loadingDelay = Math.min(800, Math.max(500, dockDelay - 300));
    this.mobileDockLoadingTimer = window.setTimeout(() => {
      if (!this.isConnected || !this.isMobileViewport() || this.mobileResultOpened) return;
      const title = this.querySelector('.fa-kc__panel--input .fa-kc__panel-head h2');
      if (title) {
        const titleTop = title.getBoundingClientRect().top + window.scrollY;
        if (window.scrollY > titleTop + 80) return;
      }
      this.classList.add('fa-kc--show-result-loading');
    }, loadingDelay);

    this.mobileDockTimer = window.setTimeout(() => {
      if (!this.isConnected || !this.isMobileViewport() || this.mobileResultOpened) return;
      const title = this.querySelector('.fa-kc__panel--input .fa-kc__panel-head h2');
      if (title) {
        const titleTop = title.getBoundingClientRect().top + window.scrollY;
        if (window.scrollY > titleTop + 80) return;
      }
      this.classList.remove('fa-kc--show-result-loading');
      this.classList.add('fa-kc--show-result-dock');
    }, dockDelay);
  }

  closeMobileResult() {
    if (this.mobileLayoutVariant !== 'result_dock') return;
    this.updateMobileViewportMetrics();
    this.mobileResultOpened = false;
    window.clearTimeout(this.mobileDockTimer);
    window.clearTimeout(this.mobileDockLoadingTimer);
    this.classList.remove('fa-kc--result-open', 'fa-kc--show-result-dock', 'fa-kc--show-result-loading');
  }

  markUserInput() {
    if (this.hasUserInput) return;
    this.hasUserInput = true;
    this.classList.add('fa-kc--has-user-input');
  }

  alignMobileInputPanel() {
    if (!this.isMobileViewport() || this.mobileLayoutVariant !== 'result_dock') return;
    this.updateMobileViewportMetrics();
    const target = this.querySelector('.fa-kc__panel--input .fa-kc__panel-head h2')
      || this.querySelector('.fa-kc__panel--input .fa-kc__panel-head')
      || this.querySelector('.fa-kc__panel--input');
    if (!target) return;

    window.clearTimeout(this.mobileAlignTimer);
    this.mobileAlignTimer = window.setTimeout(() => {
      if (!this.isConnected || !this.isMobileViewport()) return;
      const top = target.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }, 80);
  }

  handleMobileInputProgress() {
    this.scheduleDesktopIntroReveal();
    if (this.mobileLayoutVariant === 'result_dock') this.closeMobileResult();
    this.markUserInput();
    this.alignMobileInputPanel();
    this.scheduleMobileDockButton();
  }

  handleMobileScroll() {
    if (this.mobileLayoutVariant !== 'result_dock' || !this.isMobileViewport()) return;
    if (!this.classList.contains('fa-kc--show-result-dock') && !this.classList.contains('fa-kc--show-result-loading')) return;

    const title = this.querySelector('.fa-kc__panel--input .fa-kc__panel-head h2');
    if (!title) return;

    const titleTop = title.getBoundingClientRect().top + window.scrollY;
    if (window.scrollY > titleTop + 80) {
      window.clearTimeout(this.mobileDockTimer);
      window.clearTimeout(this.mobileDockLoadingTimer);
      this.classList.remove('fa-kc--show-result-dock', 'fa-kc--show-result-loading');
    }
  }

  openImageModal(imageUrl, title = '') {
    if (!imageUrl) return;

    this.closeImageModal();
    const modal = document.createElement('div');
    modal.className = 'fa-kc__image-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML = `
      <div class="fa-kc__image-modal-backdrop" data-image-modal-close></div>
      <div class="fa-kc__image-modal-dialog">
        <button type="button" class="fa-kc__image-modal-close" data-image-modal-close aria-label="${this.escapeHtml(this.t('imageClose'))}">\u00D7</button>
        <img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(title || this.t('productImage'))}">
        ${title ? `<strong>${this.escapeHtml(title)}</strong>` : ''}
      </div>
    `;
    modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-image-modal-close]')) this.closeImageModal();
    });
    this.imageModalKeyHandler = (event) => {
      if (event.key === 'Escape') this.closeImageModal();
    };
    document.addEventListener('keydown', this.imageModalKeyHandler);
    document.body.append(modal);
    this.imageModal = modal;
  }

  closeImageModal() {
    if (this.imageModalKeyHandler) {
      document.removeEventListener('keydown', this.imageModalKeyHandler);
      this.imageModalKeyHandler = null;
    }
    this.imageModal?.remove();
    this.imageModal = null;
  }

  addResumeListeners() {
    if (!this.resumeHandler || this.resumeListenersActive) return;
    window.addEventListener('pageshow', this.resumeHandler);
    window.addEventListener('focus', this.resumeHandler);
    document.addEventListener('visibilitychange', this.resumeHandler);
    this.resumeListenersActive = true;
  }

  removeResumeListeners() {
    if (!this.resumeHandler || !this.resumeListenersActive) return;
    window.removeEventListener('pageshow', this.resumeHandler);
    window.removeEventListener('focus', this.resumeHandler);
    document.removeEventListener('visibilitychange', this.resumeHandler);
    this.resumeListenersActive = false;
  }

  readConfig() {
    const script = this.querySelector('[data-keilrahmen-config]');
    if (!script?.textContent) return {};

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Canvas configurator: configuration JSON could not be parsed.', error);
      return {};
    }
  }

  handleResume() {
    if (document.hidden) return;

    window.clearTimeout(this.resumeTimer);
    this.resumeTimer = window.setTimeout(() => {
      this.applyModeConstraints();
      this.syncInputs();
      this.renderQuickSizes();

      if (this.result?.querySelector('.fa-kc__inline-status--success')) return;

      this.render();
      this.loadSupportMap();
      this.loadProductDescriptions();
    }, 80);
  }

  async loadSupportMap() {
    const endpoint = this.withLocaleParam(this.config.supportMapUrl);
    if (!endpoint) return;

    try {
      const response = await this.fetchWithTimeout(endpoint, { headers: { Accept: 'application/json' } }, 10000);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok || !payload.overrides) return;
      this.supportOverrides = payload.overrides;
      this.render();
    } catch {
      // The back preview continues to work with its standard rules.
    }
  }

  async loadProductDescriptions() {
    const endpoint = this.withLocaleParam(this.config.productDescriptionUrl);
    if (!endpoint) return;

    try {
      const response = await this.fetchWithTimeout(endpoint, { headers: { Accept: 'application/json' } }, 10000);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok || !payload.descriptions || typeof payload.descriptions !== 'object') return;

      this.productDescriptionTemplates = {
        ...(this.config.productDescriptions || {}),
        ...payload.descriptions,
      };
      this.render();
      this.updateMobileDock();
    } catch {
      // Localized fallback descriptions remain visible.
    }
  }

  normalizeLocale(locale) {
    const normalized = String(locale || 'de').toLowerCase().replace('_', '-').split('-')[0];
    return ['de', 'en', 'fr', 'it'].includes(normalized) ? normalized : 'de';
  }

  buildI18n() {
    return { ...(this.config.i18n || {}) };
  }

  t(key, vars = {}) {
    let text = this.i18n?.[key] || key;
    Object.entries(vars).forEach(([name, value]) => {
      text = text.replaceAll(`{${name}}`, String(value ?? ''));
    });
    return text;
  }

  withLocaleParam(url) {
    if (!url) return url;
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('locale', this.locale);
      return parsed.toString();
    } catch {
      const glue = String(url).includes('?') ? '&' : '?';
      return `${url}${glue}locale=${encodeURIComponent(this.locale)}`;
    }
  }

  prepareItems(items) {
    return items
      .map((item) => {
        const frameType = this.extractFrameType(item);
        const thicknessCm = this.extractThickness(item);
        const dimensions = frameType === 'round'
          ? this.extractRoundDimensions(item)
          : this.extractDimensions(item);
        if (!dimensions) return null;

        const preparedItem = {
          ...item,
          frameType,
          thicknessCm,
          diameter: dimensions.diameter || null,
          depthMm: this.extractDepthMm(item),
          inventoryQuantity: this.readNumber(item.inventoryQuantity, 0),
          width: dimensions.width,
          height: dimensions.height,
          shortSide: Math.min(dimensions.width, dimensions.height),
          longSide: Math.max(dimensions.width, dimensions.height),
          packSize: this.extractPackSize(item, thicknessCm, dimensions),
          deliveryText: '',
          deliveryLoading: false,
          deliveryRequested: false,
          deliveryError: false,
        };

        preparedItem.tierRules = this.normalizeTierRules(item.tierRules, preparedItem);
        return preparedItem;
      })
      .filter(Boolean)
      .sort(
        (a, b) =>
          this.frameSortWeight(a.frameType) - this.frameSortWeight(b.frameType) ||
          a.thicknessCm - b.thicknessCm ||
          a.shortSide - b.shortSide ||
          a.longSide - b.longSide ||
          a.price - b.price,
      );
  }

  extractFrameType(item) {
    const configured = String(item.frameType || '').trim();
    if (['studio_2cm', 'gallery_4cm', 'round'].includes(configured)) return configured;

    const sku = String(item.sku || '').trim();
    const title = `${item.productTitle || ''} ${item.variantTitle || ''}`.toLowerCase();
    if (/^kr\d{2,3}-\d{2}$/i.test(sku) || title.includes('rund') || title.includes('\u00F8')) return 'round';
    return this.extractThickness(item) === 4 ? 'gallery_4cm' : 'studio_2cm';
  }

  frameSortWeight(frameType) {
    return { studio_2cm: 1, gallery_4cm: 2, round: 3 }[frameType] || 1;
  }

  extractDimensions(item) {
    const sku = String(item.sku || '');
    const title = `${item.productTitle || ''} ${item.variantTitle || ''}`;
    const skuMatch = sku.match(/(\d{3})(\d{3})/);

    if (skuMatch) {
      return {
        width: Number.parseInt(skuMatch[1], 10),
        height: Number.parseInt(skuMatch[2], 10),
      };
    }

    const titleMatch = title.match(/(\d{2,3})\s*x\s*(\d{2,3})/i);
    if (titleMatch) {
      return {
        width: Number.parseInt(titleMatch[1], 10),
        height: Number.parseInt(titleMatch[2], 10),
      };
    }

    return null;
  }

  extractRoundDimensions(item) {
    const sku = String(item.sku || '').trim();
    const title = `${item.productTitle || ''} ${item.variantTitle || ''}`;
    const skuMatch = sku.match(/^KR(\d{2,3})-(\d{2,3})$/i);
    const titleMatch = title.match(/(?:\u00D8|Durchmesser|O)\s*(\d{2,3})\s*cm/i)
      || title.match(/(\d{2,3})\s*cm/i);
    const diameter = skuMatch
      ? Number.parseInt(skuMatch[1], 10)
      : titleMatch
        ? Number.parseInt(titleMatch[1], 10)
        : 0;

    if (!Number.isFinite(diameter) || diameter <= 0) return null;

    return {
      width: diameter,
      height: diameter,
      diameter,
    };
  }

  extractDepthMm(item) {
    const configuredDepth = Number.parseInt(item.depthMm, 10);
    if (Number.isFinite(configuredDepth) && configuredDepth > 0) return configuredDepth;

    const sku = String(item.sku || '').trim();
    const title = `${item.productTitle || ''} ${item.variantTitle || ''}`;
    const skuMatch = sku.match(/^KR\d{2,3}-(\d{2,3})$/i);
    const titleMatch = title.match(/(?:Tiefe|Profil|Profilst\u00E4rke)\s*(\d{2,3})\s*mm/i);
    return skuMatch
      ? Number.parseInt(skuMatch[1], 10)
      : titleMatch
        ? Number.parseInt(titleMatch[1], 10)
        : 42;
  }

  formatRoundDepth(item) {
    const depthMm = Number.parseInt(item?.depthMm, 10);
    const safeDepthMm = Number.isFinite(depthMm) && depthMm > 0 ? depthMm : 42;
    const depthCm = safeDepthMm / 10;
    const formatted = Number.isInteger(depthCm) ? String(depthCm) : depthCm.toFixed(1);
    return `${formatted} cm`;
  }

  extractThickness(item) {
    const configuredThickness = Number.parseInt(item.thicknessCm, 10);
    if (configuredThickness === 2 || configuredThickness === 4) return configuredThickness;

    const sku = String(item.sku || '').trim();
    const title = `${item.productTitle || ''} ${item.variantTitle || ''}`;

    if (sku.startsWith('G') || title.includes('Galerie+')) return 4;
    return 2;
  }

  extractPackSize(item, thicknessCm, dimensions) {
    if (thicknessCm === 4) return 1;

    const text = `${item.variantTitle || ''} ${item.productTitle || ''}`;
    const match = text.match(/(\d+)\s*(?:St\u00FCck|Stk\.?|Stk|Stueck)/i);
    if (match) {
      const parsed = Number.parseInt(match[1], 10);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }

    const longSide = Math.max(dimensions.width, dimensions.height);
    return longSide >= 70 ? 4 : 8;
  }

  normalizeTierRules(rules, item) {
    if (item.frameType !== 'gallery_4cm' || !Array.isArray(rules)) return [];

    const currency = String(this.config.currency || 'CHF').toUpperCase();

    return rules
      .filter((rule) => rule && rule.active !== false && String(rule.pricingType || 'FIXED_PRICE') === 'FIXED_PRICE')
      .map((rule) => {
        const tiers = Array.isArray(rule.tiers)
          ? rule.tiers
            .map((tier) => {
              const minQuantity = this.readNumber(tier.minQuantity, 0);
              const pricingValue = this.readNumber(tier.pricingValue ?? tier.fixedVpePriceCents, 0);
              const tierCurrency = String(tier.currency || currency).toUpperCase();

              return {
                minQuantity,
                pricingValue,
                fixedVpePriceCents: this.readNumber(tier.fixedVpePriceCents ?? pricingValue, pricingValue),
                currency: tierCurrency,
                vpeLabel: String(tier.vpeLabel || tier.label || '').trim(),
              };
            })
            .filter((tier) => tier.minQuantity > 0 && tier.pricingValue > 0 && tier.currency === currency)
            .sort((left, right) => left.minQuantity - right.minQuantity)
          : [];

        return {
          ...rule,
          priority: this.readNumber(rule.priority, 0),
          tiers,
        };
      })
      .filter((rule) => rule.tiers.length > 0)
      .sort((left, right) => right.priority - left.priority);
  }

  selectedTierRule(item) {
    return Array.isArray(item.tierRules) && item.tierRules.length > 0 ? item.tierRules[0] : null;
  }

  hasTierPrices(item) {
    return item.frameType === 'gallery_4cm' && this.selectedTierRule(item) !== null;
  }

  quantityKey(item) {
    return String(item.id || item.sku || `${item.thicknessCm}:${item.shortSide}x${item.longSide}`);
  }

  quantityForItem(item) {
    const quantity = this.readNumber(this.quantities[this.quantityKey(item)], 1);
    return this.clamp(quantity, 1, 999);
  }

  setQuantityForItem(item, quantity) {
    this.quantities[this.quantityKey(item)] = this.clamp(this.readNumber(quantity, 1), 1, 999);
  }

  applicableTier(item, quantity = this.quantityForItem(item)) {
    const rule = this.selectedTierRule(item);
    if (!rule) return null;

    return rule.tiers.reduce((selected, tier) => (
      quantity >= tier.minQuantity ? tier : selected
    ), null);
  }

  getItemsForThickness() {
    if (this.isRoundMode()) return this.items.filter((item) => item.frameType === 'round');
    return this.items.filter((item) => item.frameType !== 'round' && item.thicknessCm === this.state.thicknessCm);
  }

  getCombosForThickness() {
    if (this.isRoundMode()) {
      return this.getRoundDiameters().map((diameter) => ({ length: diameter, width: diameter }));
    }

    const sourceItems = this.getItemsForThickness();
    const seen = new Set();
    const combos = [];

    sourceItems.forEach((item) => {
      [
        { length: item.width, width: item.height },
        { length: item.height, width: item.width },
      ].forEach((combo) => {
        const key = `${combo.length}x${combo.width}`;
        if (seen.has(key)) return;
        seen.add(key);
        combos.push(combo);
      });
    });

    return combos.sort((a, b) => a.length - b.length || a.width - b.width);
  }

  getAxisValues(axis) {
    if (this.isRoundMode()) return axis === 'length' ? this.getRoundDiameters() : [];

    const values = new Set();
    this.getCombosForThickness().forEach((combo) => values.add(combo[axis]));
    if (this.state.thicknessCm === 4 && values.size) {
      for (let value = 200; value <= 300; value += 10) values.add(value);
    }
    return [...values].sort((a, b) => a - b);
  }

  getRoundDiameters() {
    return [...new Set(this.getItemsForThickness().map((item) => item.diameter || item.shortSide).filter(Boolean))]
      .sort((a, b) => a - b);
  }

  isRoundMode() {
    return this.state.frameType === 'round';
  }

  updateModeUi() {
    const isRound = this.isRoundMode();
    this.classList.toggle('fa-kc--round-mode', isRound);

    if (this.lengthTitle) this.lengthTitle.textContent = isRound ? this.t('stepDiameter') : this.t('stepLength');
    if (this.widthTitle) this.widthTitle.textContent = this.t('stepWidth');
    if (this.lengthNumber) this.lengthNumber.setAttribute('aria-label', isRound ? this.t('diameterCm') : this.t('lengthCm'));
    if (this.lengthRange) this.lengthRange.setAttribute('aria-label', isRound ? this.t('diameterCm') : this.t('lengthCm'));
    if (this.widthCard) this.widthCard.hidden = isRound;

    this.thicknessInputs.forEach((input) => {
      input.checked = isRound
        ? input.value === 'round'
        : Number.parseInt(input.value, 10) === this.state.thicknessCm;
    });

    if (this.includeCustomInput) {
      this.includeCustomInput.disabled = isRound;
      this.includeCustomInput.checked = isRound ? false : this.state.includeCustom;
    }

    if (this.customToggle) {
      this.customToggle.classList.toggle('is-disabled', isRound);
      this.customToggle.title = isRound ? this.t('customRoundDisabled') : '';
    }
  }

  applyModeConstraints() {
    this.updateModeUi();

    if (this.isRoundMode()) {
      this.state.includeCustom = false;
      if (this.includeCustomInput) this.includeCustomInput.checked = false;

      const diameters = this.getRoundDiameters();
      if (!diameters.length) {
        this.configureNumberInput(this.lengthNumber, 0, 0);
        this.configureRangeInput(this.lengthRange, 0, 0, 1, 0);
        this.renderTicks('length', [], 0, 0);
        this.syncInputs();
        return;
      }

      this.state.length = this.nearestAllowedValue(this.state.length, diameters);
      this.state.width = this.state.length;
      this.configureNumberInput(this.lengthNumber, diameters[0], diameters[diameters.length - 1]);
      this.configureRangeInput(this.lengthRange, 0, diameters.length - 1, 1, this.indexForAllowedValue(this.state.length, diameters));
      this.renderTicks('length', this.getTickValues(diameters), 0, diameters.length - 1, diameters, diameters);
      this.syncInputs();
      return;
    }

    if (this.state.includeCustom) {
      const max = this.customInputMax();
      const numberMax = this.state.thicknessCm === 4 ? 10000 : 195;
      this.state.length = this.clamp(this.state.length, 15, numberMax);
      this.state.width = this.clamp(this.state.width, 15, numberMax);
      this.configureNumberInput(this.lengthNumber, 15, numberMax);
      this.configureNumberInput(this.widthNumber, 15, numberMax);
      this.configureRangeInput(this.lengthRange, 15, max, 1, Math.min(this.state.length, max));
      this.configureRangeInput(this.widthRange, 15, max, 1, Math.min(this.state.width, max));
      const ticks = max === 300 ? [15, 100, 195, 250, 300] : [15, 50, 100, 150, 195];
      this.renderTicks('length', ticks, 15, max, null, this.customTickValues());
      this.renderTicks('width', ticks, 15, max, null, this.customTickValues());
      this.syncInputs();
      return;
    }

    const combos = this.getCombosForThickness();
    if (!combos.length) {
      this.configureNumberInput(this.lengthNumber, 15, 195);
      this.configureNumberInput(this.widthNumber, 15, 195);
      this.configureRangeInput(this.lengthRange, 0, 0, 1, 0);
      this.configureRangeInput(this.widthRange, 0, 0, 1, 0);
      this.renderTicks('length', [], 0, 0);
      this.renderTicks('width', [], 0, 0);
      this.syncInputs();
      return;
    }

    const nearestCombo = this.nearestCombo(this.state.length, this.state.width, combos);
    this.state.length = nearestCombo.length;
    this.state.width = nearestCombo.width;
    this.configureDiscreteAxis('length');
    this.configureDiscreteAxis('width');
    this.syncInputs();
  }

  configureDiscreteAxis(axis) {
    const values = this.getAxisValues(axis);
    const numberInput = axis === 'length' ? this.lengthNumber : this.widthNumber;
    const rangeInput = axis === 'length' ? this.lengthRange : this.widthRange;

    if (!values.length) return;

    this.configureNumberInput(numberInput, values[0], values[values.length - 1]);
    this.configureRangeInput(rangeInput, 0, values.length - 1, 1, this.indexForAllowedValue(this.state[axis], values));
    const ticks = this.state.thicknessCm === 4
      ? [15, 50, 100, 150, 195, 250, 300].filter((value) => values.includes(value))
      : this.getTickValues(values);
    this.renderTicks(axis, ticks, 0, values.length - 1, values, values);
  }

  configureNumberInput(input, min, max) {
    if (!input) return;
    input.min = String(min);
    input.max = String(max);
    input.step = '1';
  }

  configureRangeInput(input, min, max, step, value) {
    if (!input) return;
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(value);
  }

  syncInputs() {
    if (this.lengthNumber) this.lengthNumber.value = String(this.state.length);
    if (this.widthNumber) this.widthNumber.value = String(this.state.width);

    if (this.isRoundMode()) {
      const diameters = this.getRoundDiameters();
      if (this.lengthRange && diameters.length) {
        this.lengthRange.value = String(this.indexForAllowedValue(this.state.length, diameters));
        this.lengthRange.setAttribute('aria-valuetext', `\u00D8 ${this.state.length} cm`);
        this.updateSwipeCue('length', this.lengthRange);
      }
      return;
    }

    if (this.state.includeCustom) {
      if (this.lengthRange) {
        this.lengthRange.value = String(this.state.length);
        this.lengthRange.setAttribute('aria-valuetext', `${this.state.length} cm`);
        this.updateSwipeCue('length', this.lengthRange);
      }
      if (this.widthRange) {
        this.widthRange.value = String(this.state.width);
        this.widthRange.setAttribute('aria-valuetext', `${this.state.width} cm`);
        this.updateSwipeCue('width', this.widthRange);
      }
      return;
    }

    const lengthValues = this.getAxisValues('length');
    const widthValues = this.getAxisValues('width');

    if (this.lengthRange && lengthValues.length) {
      this.lengthRange.value = String(this.indexForAllowedValue(this.state.length, lengthValues));
      this.lengthRange.setAttribute('aria-valuetext', `${this.state.length} cm`);
      this.updateSwipeCue('length', this.lengthRange);
    }

    if (this.widthRange && widthValues.length) {
      this.widthRange.value = String(this.indexForAllowedValue(this.state.width, widthValues));
      this.widthRange.setAttribute('aria-valuetext', `${this.state.width} cm`);
      this.updateSwipeCue('width', this.widthRange);
    }
  }

  updateSwipeCue(axis, input) {
    const cue = input?.closest('.fa-kc__range-wrap')?.querySelector('.fa-kc__swipe-icon');
    if (!cue) return;

    const min = this.readNumber(input.min, 0);
    const max = this.readNumber(input.max, 0);
    const value = this.readNumber(input.value, min);
    const percent = max > min ? ((value - min) / (max - min)) * 100 : 50;
    cue.style.setProperty('--fa-kc-swipe-left', `${this.clamp(percent, 7, 93)}%`);
  }

  handleRangeInput(axis) {
    if (this.isRoundMode()) {
      const values = this.getRoundDiameters();
      const index = this.clamp(this.readNumber(this.lengthRange?.value, 0), 0, Math.max(values.length - 1, 0));
      this.state.length = values[index] || this.state.length;
      this.state.width = this.state.length;
      this.syncInputs();
      this.render();
      this.updateMobileDock();
      this.handleMobileInputProgress();
      this.scheduleMobileResultReveal('range');
      return;
    }

    if (this.state.includeCustom) {
      const input = axis === 'length' ? this.lengthRange : this.widthRange;
      this.state[axis] = this.clamp(this.readNumber(input?.value, this.state[axis]), 15, this.customInputMax());
    } else {
      const input = axis === 'length' ? this.lengthRange : this.widthRange;
      const values = this.getAxisValues(axis);
      const index = this.clamp(this.readNumber(input?.value, 0), 0, Math.max(values.length - 1, 0));
      this.state[axis] = values[index] || this.state[axis];
      if (this.activateCustomForLargeSize(this.state[axis])) {
        this.applyModeConstraints();
      } else {
        this.snapPartnerToCombination(axis);
        this.configureDiscreteAxis(axis === 'length' ? 'width' : 'length');
      }
    }

    this.syncInputs();
    this.render();
    this.updateMobileDock();
    this.handleMobileInputProgress();
    this.scheduleMobileResultReveal('range');
  }

  handleNumberInput(axis, event, force = false) {
    if (!force && event?.inputType) {
      this.handleMobileInputProgress();
      return;
    }

    const input = axis === 'length' ? this.lengthNumber : this.widthNumber;
    const rawValue = this.readNumber(input?.value, this.state[axis]);

    if (this.isRoundMode()) {
      const values = this.getRoundDiameters();
      this.state.length = values.length ? this.nearestAllowedValue(rawValue, values) : rawValue;
      this.state.width = this.state.length;
      this.applyModeConstraints();
      this.render();
      this.updateMobileDock();
      this.handleMobileInputProgress();
      this.scheduleMobileResultReveal('input');
      return;
    }

    this.activateCustomForLargeSize(rawValue);
    if (this.state.includeCustom) {
      this.state[axis] = this.clamp(Math.round(rawValue), 15, this.state.thicknessCm === 4 ? 10000 : 195);
    } else {
      const values = this.getAxisValues(axis);
      this.state[axis] = values.length ? this.nearestAllowedValue(rawValue, values) : rawValue;
      this.snapPartnerToCombination(axis);
      this.configureDiscreteAxis(axis === 'length' ? 'width' : 'length');
    }

    this.applyModeConstraints();
    this.render();
    this.updateMobileDock();
    this.handleMobileInputProgress();
    this.scheduleMobileResultReveal('input');
  }

  handleStepperClick(button) {
    const axis = button?.dataset?.stepper;
    const direction = this.readNumber(button?.dataset?.step, 0);
    if (!['length', 'width'].includes(axis) || direction === 0) return;

    if (this.isRoundMode()) {
      if (axis !== 'length') return;
      const values = this.getRoundDiameters();
      if (!values.length) return;
      const currentIndex = this.indexForAllowedValue(this.state.length, values);
      const nextIndex = this.clamp(currentIndex + direction, 0, values.length - 1);
      this.state.length = values[nextIndex] || this.state.length;
      this.state.width = this.state.length;
      this.applyModeConstraints();
      this.render();
      this.updateMobileDock();
      this.handleMobileInputProgress();
      this.scheduleMobileResultReveal('stepper');
      return;
    }

    if (this.state.includeCustom) {
      this.state[axis] = this.clamp(this.state[axis] + direction, 15, this.customInputMax());
    } else {
      const values = this.getAxisValues(axis);
      if (!values.length) return;

      const currentIndex = this.indexForAllowedValue(this.state[axis], values);
      const nextIndex = this.clamp(currentIndex + direction, 0, values.length - 1);
      this.state[axis] = values[nextIndex] || this.state[axis];
      if (!this.activateCustomForLargeSize(this.state[axis])) {
        this.snapPartnerToCombination(axis);
        this.configureDiscreteAxis(axis === 'length' ? 'width' : 'length');
      }
    }

    this.applyModeConstraints();
    this.render();
    this.updateMobileDock();
    this.handleMobileInputProgress();
    this.scheduleMobileResultReveal('stepper');
  }

  handleThicknessChange(input) {
    if (!input.checked) return;

    if (input.value === 'round') {
      this.state.frameType = 'round';
      this.state.thicknessCm = 4;
      this.state.includeCustom = false;
    } else {
      this.state.thicknessCm = Number.parseInt(input.value, 10) === 4 ? 4 : 2;
      this.state.frameType = this.state.thicknessCm === 4 ? 'gallery_4cm' : 'studio_2cm';
    }

    this.applyModeConstraints();
    this.renderQuickSizes();
    this.render();
    this.updateMobileDock();
    this.handleMobileInputProgress();
    this.scheduleMobileResultReveal('choice');
  }

  handleCustomToggle() {
    if (this.isRoundMode()) {
      this.state.includeCustom = false;
      if (this.includeCustomInput) this.includeCustomInput.checked = false;
      this.applyModeConstraints();
      this.renderQuickSizes();
      this.render();
      this.updateMobileDock();
      return;
    }

    this.state.includeCustom = Boolean(this.includeCustomInput?.checked);
    this.applyModeConstraints();
    this.renderQuickSizes();
    this.render();
    this.updateMobileDock();
    this.handleMobileInputProgress();
    this.scheduleMobileResultReveal('choice');
  }

  snapPartnerToCombination(axis) {
    const partnerAxis = axis === 'length' ? 'width' : 'length';
    const combos = this.getCombosForThickness().filter((combo) => combo[axis] === this.state[axis]);
    if (!combos.length) {
      const nearestCombo = this.nearestCombo(this.state.length, this.state.width, this.getCombosForThickness());
      if (nearestCombo) {
        this.state.length = nearestCombo.length;
        this.state.width = nearestCombo.width;
      }
      return;
    }

    const currentPartnerValue = this.state[partnerAxis];
    const nearestPartner = combos.reduce((nearest, combo) => {
      const currentDistance = Math.abs(combo[partnerAxis] - currentPartnerValue);
      const nearestDistance = Math.abs(nearest[partnerAxis] - currentPartnerValue);
      return currentDistance < nearestDistance ? combo : nearest;
    }, combos[0]);

    this.state[partnerAxis] = nearestPartner[partnerAxis];
  }

  findMatches(length, width) {
    if (this.isRoundMode()) {
      const diameter = Number(length);
      return this.getItemsForThickness().filter((item) => Number(item.diameter || item.shortSide) === diameter);
    }

    const shortSide = Math.min(length, width);
    const longSide = Math.max(length, width);

    return this.getItemsForThickness().filter((item) => item.shortSide === shortSide && item.longSide === longSide);
  }

  renderQuickSizes() {
    if (!this.quickSizes) return;

    if (this.isRoundMode()) {
      const manualDiameters = this.parseQuickDiameters(this.config.quickSizes?.round || '');
      const sourceDiameters = manualDiameters.length ? manualDiameters : this.getRoundDiameters();
      const diameters = sourceDiameters
        .filter((diameter) => this.getItemsForThickness().some((item) => Number(item.diameter || item.shortSide) === diameter))
        .slice(0, 8);

      if (!diameters.length) {
        this.quickSizes.innerHTML = '';
        return;
      }

      this.quickSizes.innerHTML = `
        <span class="fa-kc__quick-label">${this.escapeHtml(this.t('quickDiameters'))} ${this.renderHelp(this.t('quickDiametersHelp'))}</span>
        <div class="fa-kc__quick-buttons">
          ${diameters
            .map((diameter) => `<button type="button" data-length="${diameter}" data-width="${diameter}">\u00D8 ${diameter} cm</button>`)
            .join('')}
        </div>
      `;
      return;
    }

    const manualSizes = this.parseQuickSizes(this.config.quickSizes?.[String(this.state.thicknessCm)] || '');
    const sourceSizes = manualSizes.length ? manualSizes : this.getFallbackQuickSizes();
    const sizes = sourceSizes
      .map((size) => this.findQuickSize(size.length, size.width))
      .filter(Boolean)
      .slice(0, 8);

    if (!sizes.length) {
      this.quickSizes.innerHTML = '';
      return;
    }

    this.quickSizes.innerHTML = `
      <span class="fa-kc__quick-label">${this.escapeHtml(this.t('quickFormats', { thickness: this.state.thicknessCm }))} ${this.renderHelp(this.t('quickFormatsHelp'))}</span>
      <div class="fa-kc__quick-buttons">
        ${sizes
          .map((size) => `<button type="button" data-length="${size.length}" data-width="${size.width}">${size.length} x ${size.width}</button>`)
          .join('')}
      </div>
    `;
  }

  parseQuickSizes(value) {
    return String(value || '')
      .split(/[\n,;]+/)
      .map((entry) => entry.trim().match(/^(\d{2,3})\s*x\s*(\d{2,3})$/i))
      .filter(Boolean)
      .map((match) => ({
        length: Number.parseInt(match[1], 10),
        width: Number.parseInt(match[2], 10),
      }));
  }

  parseQuickDiameters(value) {
    return String(value || '')
      .split(/[\n,;]+/)
      .map((entry) => Number.parseInt(entry.trim().replace(/^\u00D8\s*/i, ''), 10))
      .filter((value) => Number.isFinite(value) && value > 0);
  }

  getFallbackQuickSizes() {
    const seen = new Set();
    return this.getItemsForThickness()
      .map((item) => ({ length: item.shortSide, width: item.longSide }))
      .filter((size) => {
        const key = `${size.length}x${size.width}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  findQuickSize(length, width) {
    const shortSide = Math.min(length, width);
    const longSide = Math.max(length, width);
    const match = this.getItemsForThickness().find(
      (item) => item.shortSide === shortSide && item.longSide === longSide,
    );

    return match ? { length, width } : null;
  }

  renderTicks(axis, majorValues, min, max, discreteValues = null, minorValues = []) {
    const target = axis === 'length' ? this.lengthTicks : this.widthTicks;
    if (!target) return;

    if (!minorValues.length || min === max) {
      target.innerHTML = '';
      this.setTrackMarks(axis, []);
      return;
    }

    const mobileMajorValues = this.isMobileViewport() ? this.mobileTickValues(majorValues, min, max) : null;
    const majorSet = new Set(mobileMajorValues || majorValues);
    const mobileMajorPositions = mobileMajorValues && mobileMajorValues.length > 1
      ? new Map(mobileMajorValues.map((value, index) => [value, (index / (mobileMajorValues.length - 1)) * 100]))
      : null;
    const positions = [];
    target.innerHTML = minorValues
      .map((value) => {
        const positionValue = discreteValues ? discreteValues.indexOf(value) : value;
        if (positionValue < min) return '';

        const actualLeft = ((positionValue - min) / (max - min)) * 100;
        const isMajor = majorSet.has(value);
        const left = mobileMajorPositions?.has(value) ? mobileMajorPositions.get(value) : actualLeft;
        positions.push(actualLeft);
        return `
          <span class="fa-kc__tick ${isMajor ? 'fa-kc__tick--major' : 'fa-kc__tick--minor'}" style="left: ${left}%">
            <i aria-hidden="true"></i>
            ${isMajor ? `<b>${value}</b>` : ''}
          </span>
        `;
      })
      .join('');
    this.setTrackMarks(axis, positions);
  }

  renderHelp(text) {
    return `<span class="fa-kc__help" tabindex="0" role="button" aria-label="${this.escapeHtml(text)}" data-help="${this.escapeHtml(text)}">?</span>`;
  }

  mobileTickValues(values, min = 0, max = 0) {
    if (this.isRoundMode()) {
      const roundValues = [...new Set(values)].sort((a, b) => a - b);
      if (roundValues.length <= 5) return roundValues;

      const lastIndex = roundValues.length - 1;
      return [0, 0.25, 0.5, 0.75, 1]
        .map((ratio) => roundValues[Math.round(lastIndex * ratio)])
        .filter((value, index, list) => index === 0 || value !== list[index - 1]);
    }

    if (this.mobileLayoutVariant === 'result_dock') {
      if (min === 15 && max === 195) return [15, 50, 100, 150, 195];
      return this.state.thicknessCm === 4 ? [15, 50, 100, 150, 195] : [20, 50, 100, 140];
    }

    const uniqueValues = [...new Set(values)].sort((a, b) => a - b);
    if (uniqueValues.length <= 5) return uniqueValues;

    const lastIndex = uniqueValues.length - 1;
    return [0, 0.25, 0.5, 0.75, 1]
      .map((ratio) => uniqueValues[Math.round(lastIndex * ratio)])
      .filter((value, index, list) => index === 0 || value !== list[index - 1]);
  }

  setTrackMarks(axis, positions) {
    const input = axis === 'length' ? this.lengthRange : this.widthRange;
    if (!input) return;

    const base = 'color-mix(in srgb, var(--fa-kc-brand) 11%, #fff)';
    const uniquePositions = [...new Set(positions.map((position) => position.toFixed(3)))];
    if (!uniquePositions.length) {
      input.style.setProperty('--fa-kc-track-background', base);
      return;
    }

    const marks = uniquePositions
      .map(
        (position) =>
          `linear-gradient(90deg, transparent calc(${position}% - 0.5px), rgba(8, 0, 139, 0.46) calc(${position}% - 0.5px), rgba(8, 0, 139, 0.46) calc(${position}% + 0.5px), transparent calc(${position}% + 0.5px))`,
      )
      .join(', ');

    input.style.setProperty('--fa-kc-track-background', `${marks}, ${base}`);
  }

  getTickValues(values) {
    if (values.length <= 7) return values;

    const indexes = new Set([0, values.length - 1]);
    const step = Math.max(1, Math.round((values.length - 1) / 5));
    for (let index = step; index < values.length - 1; index += step) {
      indexes.add(index);
    }

    return [...indexes].sort((a, b) => a - b).map((index) => values[index]);
  }

  render() {
    if (!this.result) return;

    if (!this.items.length) {
      this.result.innerHTML = `
        <div class="fa-kc__empty">
          <h2>${this.escapeHtml(this.t('noVariantsTitle'))}</h2>
          <p>${this.escapeHtml(this.t('noVariantsText'))}</p>
        </div>
      `;
      return;
    }

    const { length, width, thicknessCm } = this.state;
    if (!length || !width) {
      this.result.innerHTML = `
        <div class="fa-kc__empty">
          <h2>${this.escapeHtml(this.t('inputRequiredTitle'))}</h2>
          <p>${this.escapeHtml(this.t('invalidSize'))}</p>
        </div>
      `;
      return;
    }

    if (this.state.includeCustom) {
      this.renderCustomSelection(length, width);
      return;
    }

    const matches = this.findMatches(length, width);
    if (!matches.length) {
      this.renderNoMatch(length, width);
      return;
    }

    const availableMatches = matches.filter((item) => this.isInStock(item));
    const unavailableMatches = matches.filter((item) => !this.isInStock(item));

    this.result.innerHTML = `
      ${this.renderPreview(length, width, thicknessCm)}
      <div class="fa-kc__matches">
        ${availableMatches.map((item) => this.renderMatch(item, length, width)).join('')}
        ${unavailableMatches.map((item) => this.renderMatch(item, length, width)).join('')}
      </div>
    `;
    this.updateMobileDock();
  }

  renderPreview(length, width, thicknessCm) {
    if (this.isRoundMode()) return this.renderRoundPreview(length);

    const maxSide = Math.max(length, width);
    const maxPreviewSize = this.mobileLayoutVariant === 'result_dock' && this.isMobileViewport() ? 252 : 180;
    const previewWidth = (length / maxSide) * maxPreviewSize;
    const previewHeight = (width / maxSide) * maxPreviewSize;
    const previewRatio = length / width;
    const railSize = thicknessCm === 4 ? 12 : 8;
    const labelAspectRatio = 1000 / 680;
    const labelMarketingScale = 1.65;
    const labelWidthFromFormat = previewWidth * (30 / length) * labelMarketingScale;
    const labelWidthFromHeight = previewHeight * (20 / width) * labelAspectRatio * labelMarketingScale;
    const labelWidth = Math.min(
      labelWidthFromFormat,
      labelWidthFromHeight,
      previewWidth * 0.74,
      previewHeight * 0.58 * labelAspectRatio,
      142,
    );
    const labelImage = this.config.labelImage
      ? `<img class="fa-kc__preview-label" src="${this.escapeHtml(this.config.labelImage)}" alt="" aria-hidden="true" loading="lazy">`
      : '';
    const supportType = this.supportTypeFor(length, width, thicknessCm);
    const supportLabel = this.supportLabel(supportType);
    const previewStyle = `--preview-width: ${previewWidth.toFixed(2)}px; --preview-height: ${previewHeight.toFixed(2)}px; --preview-ratio: ${previewRatio.toFixed(4)}; --preview-rail: ${railSize}px; --preview-label-width: ${labelWidth.toFixed(2)}px;`;
    const backLabel = this.config.showBackSupportCaption === false
      ? ''
      : `<span class="fa-kc__back-label">${this.escapeHtml(supportLabel)}</span>`;

    return `
      <div class="fa-kc__preview" aria-label="${this.escapeHtml(this.t('previewRect'))}">
        <div class="fa-kc__preview-meta">
          <strong>${this.escapeHtml(this.t('previewTitle'))}</strong>
        </div>
        <div class="fa-kc__preview-visuals">
          <div class="fa-kc__preview-stage">
            <div class="fa-kc__preview-measured" style="${previewStyle}">
              <span class="fa-kc__preview-measure fa-kc__preview-measure--top"><b>${this.escapeHtml(length)} cm</b></span>
              <span class="fa-kc__preview-measure fa-kc__preview-measure--left"><b>${this.escapeHtml(width)} cm</b></span>
              <div class="fa-kc__preview-frame">
                <span class="fa-kc__preview-canvas"></span>
                ${labelImage}
              </div>
            </div>
          </div>
          <div class="fa-kc__preview-stage fa-kc__preview-stage--back">
            ${backLabel}
            ${this.renderBackPreview(previewWidth, previewHeight, previewRatio, railSize, supportType)}
          </div>
        </div>
      </div>
    `;
  }

  renderRoundPreview(diameter) {
    const maxPreviewSize = this.mobileLayoutVariant === 'result_dock' && this.isMobileViewport() ? 252 : 180;
    const previewSize = Math.min(maxPreviewSize, Math.max(96, maxPreviewSize * 0.88));
    const labelImage = this.config.labelImage
      ? `<img class="fa-kc__preview-label fa-kc__preview-label--round" src="${this.escapeHtml(this.config.labelImage)}" alt="" aria-hidden="true" loading="lazy">`
      : '';

    return `
      <div class="fa-kc__preview fa-kc__preview--round" aria-label="${this.escapeHtml(this.t('previewRound'))}">
        <div class="fa-kc__preview-meta">
          <strong>${this.escapeHtml(this.t('previewTitle'))}</strong>
        </div>
        <div class="fa-kc__preview-visuals">
          <div class="fa-kc__preview-stage">
            <div class="fa-kc__preview-round-measured" style="--preview-size: ${previewSize.toFixed(2)}px;">
              <span class="fa-kc__preview-measure fa-kc__preview-measure--diameter"><b>\u00D8 ${this.escapeHtml(diameter)} cm</b></span>
              <div class="fa-kc__preview-round-frame">
                <span class="fa-kc__preview-round-canvas"></span>
                ${labelImage}
              </div>
            </div>
          </div>
          <div class="fa-kc__preview-stage fa-kc__preview-stage--round-side">
            <span class="fa-kc__back-label">${this.escapeHtml(this.t('depth'))} 4.2 cm</span>
            <div class="fa-kc__round-profile" aria-hidden="true"><span></span></div>
          </div>
        </div>
      </div>
    `;
  }

  renderBackPreview(previewWidth, previewHeight, previewRatio, railSize, supportType) {
    const landscape = previewWidth >= previewHeight;
    const crossbarDirection = landscape ? 'vertical' : 'horizontal';
    const querleisteClass = `fa-kc__back-strut fa-kc__back-strut--${crossbarDirection}`;
    const crossCount = { doppelkreuz: 2, dreifachkreuz: 3 }[supportType] || 0;
    const struts = crossCount
      ? `<span class="fa-kc__back-strut fa-kc__back-strut--${landscape ? 'horizontal' : 'vertical'}"></span>
        <span class="fa-kc__back-crossbars${landscape ? '' : ' fa-kc__back-crossbars--portrait'}">
          ${Array.from({ length: crossCount }, () => `<span class="fa-kc__back-strut fa-kc__back-strut--${crossbarDirection}"></span>`).join('')}
        </span>`
      : {
      none: '',
      querleiste: `<span class="${querleisteClass}"></span>`,
      kreuz: '<span class="fa-kc__back-strut fa-kc__back-strut--horizontal"></span><span class="fa-kc__back-strut fa-kc__back-strut--vertical"></span>',
    }[supportType] || '';

    return `
      <div
        class="fa-kc__back-frame fa-kc__back-frame--${this.escapeHtml(supportType)}"
        style="--preview-width: ${previewWidth.toFixed(2)}px; --preview-height: ${previewHeight.toFixed(2)}px; --preview-ratio: ${previewRatio.toFixed(4)}; --preview-rail: ${railSize}px;"
      >
        <span class="fa-kc__back-canvas"></span>
        ${struts}
      </div>
    `;
  }

  renderMatch(item, inputLength, inputWidth) {
    if (item.frameType === 'round') return this.renderRoundMatch(item, inputLength);

    const inStock = this.isInStock(item);
    const hasVariantTitle = item.variantTitle && !/^default title$/i.test(String(item.variantTitle).trim());
    const displayTitle = hasVariantTitle ? item.variantTitle : item.productTitle;
    const subtitle = hasVariantTitle ? item.productTitle : (item.sku || 'Standard');
    const image = item.image
      ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.productTitle)}" loading="lazy">`
      : '<div class="fa-kc__image-placeholder" aria-hidden="true"></div>';

    return `
      <article class="fa-kc__match ${inStock ? '' : 'fa-kc__match--disabled'}">
        <button
          type="button"
          class="fa-kc__match-media"
          data-preview-image="${this.escapeHtml(item.image || '')}"
          data-preview-title="${this.escapeHtml(displayTitle)}"
          aria-label="${this.escapeHtml(this.t('imageZoom'))}"
        >
          ${image}
        </button>
        <div class="fa-kc__match-body">
          <h3>${this.escapeHtml(displayTitle)}</h3>
          <p class="fa-kc__match-subtitle">${this.escapeHtml(subtitle)}</p>
          <div class="fa-kc__match-mobile-meta">
            <span>${item.shortSide} x ${item.longSide} cm</span>
            <span>${item.thicknessCm} cm ${this.escapeHtml(this.t('thickness'))}</span>
          </div>
          <dl>
            <div><dt>${this.escapeHtml(this.t('sku'))}</dt><dd>${this.escapeHtml(item.sku || '-')}</dd></div>
            <div><dt>${this.escapeHtml(this.t('format'))}</dt><dd>${item.shortSide} x ${item.longSide} cm</dd></div>
            <div><dt>${this.escapeHtml(this.t('thickness'))}</dt><dd>${item.thicknessCm} cm</dd></div>
            <div><dt>${this.escapeHtml(this.t('vpe'))}</dt><dd>${this.escapeHtml(this.packLabel(item))}</dd></div>
            <div class="fa-kc__stock-cell"><dt>${this.escapeHtml(this.t('availability'))} ${this.renderAvailabilityHelp(item)}</dt><dd>${this.renderStockStatus(item)}</dd></div>
            <div class="fa-kc__delivery-cell"><dt>${this.escapeHtml(this.t('deliveryDate'))} ${this.renderHelp(this.t('deliveryHelp'))}</dt><dd>${this.renderDeliveryStatus(item)}</dd></div>
          </dl>
          ${this.renderProductDescription(item)}
          ${this.renderPriceBlockWithBenefits(item)}
          ${inStock ? this.renderTierControls(item) : ''}
          <div class="fa-kc__match-actions">
            ${inStock ? this.renderCartButton(item, inputLength, inputWidth) : this.renderNotifyForm(item, inputLength, inputWidth)}
            <a href="${this.escapeHtml(item.url)}">${this.escapeHtml(this.t('viewProduct'))}</a>
          </div>
          ${this.renderShareButton(item.image || '')}
        </div>
      </article>
    `;
  }

  renderRoundMatch(item, inputDiameter) {
    const inStock = this.isInStock(item);
    const diameter = item.diameter || item.shortSide || inputDiameter;
    const depthLabel = this.formatRoundDepth(item);
    const displayTitle = this.displayTitleForItem(item);
    const subtitle = item.productTitle || this.t('roundTitle');
    const image = item.image
      ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(displayTitle)}" loading="lazy">`
      : '<div class="fa-kc__image-placeholder" aria-hidden="true"></div>';

    return `
      <article class="fa-kc__match ${inStock ? '' : 'fa-kc__match--disabled'} fa-kc__match--round">
        <button
          type="button"
          class="fa-kc__match-media"
          data-preview-image="${this.escapeHtml(item.image || '')}"
          data-preview-title="${this.escapeHtml(displayTitle)}"
          aria-label="${this.escapeHtml(this.t('imageZoom'))}"
        >
          ${image}
        </button>
        <div class="fa-kc__match-body">
          <h3>${this.escapeHtml(displayTitle)}</h3>
          <p class="fa-kc__match-subtitle">${this.escapeHtml(subtitle)}</p>
          <div class="fa-kc__match-mobile-meta">
            <span>\u00D8 ${diameter} cm</span>
            <span>${this.escapeHtml(this.t('depth'))} ${this.escapeHtml(depthLabel)}</span>
          </div>
          <dl>
            <div><dt>${this.escapeHtml(this.t('sku'))}</dt><dd>${this.escapeHtml(item.sku || '-')}</dd></div>
            <div><dt>${this.escapeHtml(this.t('diameter'))}</dt><dd>\u00D8 ${diameter} cm</dd></div>
            <div><dt>${this.escapeHtml(this.t('depth'))}</dt><dd>${this.escapeHtml(depthLabel)}</dd></div>
            <div><dt>${this.escapeHtml(this.t('vpe'))}</dt><dd>${this.escapeHtml(this.packLabel(item))}</dd></div>
            <div class="fa-kc__stock-cell"><dt>${this.escapeHtml(this.t('availability'))} ${this.renderAvailabilityHelp(item)}</dt><dd>${this.renderStockStatus(item)}</dd></div>
            <div class="fa-kc__delivery-cell"><dt>${this.escapeHtml(this.t('deliveryDate'))} ${this.renderHelp(this.t('deliveryHelp'))}</dt><dd>${this.renderDeliveryStatus(item)}</dd></div>
          </dl>
          ${this.renderProductDescription(item)}
          ${this.renderPriceBlockWithBenefits(item)}
          <div class="fa-kc__match-actions">
            ${inStock ? this.renderCartButton(item, diameter, diameter) : this.renderNotifyForm(item, diameter, diameter)}
            <a href="${this.escapeHtml(item.url)}">${this.escapeHtml(this.t('viewProduct'))}</a>
          </div>
          ${this.renderShareButton(item.image || '')}
        </div>
      </article>
    `;
  }

  renderCartButton(item, inputLength, inputWidth) {
    return `
      <button
        type="button"
        data-add-variant="${this.escapeHtml(item.id)}"
        data-input-length="${inputLength}"
        data-input-width="${inputWidth}"
        data-thickness="${item.thicknessCm}"
        data-frame-type="${this.escapeHtml(item.frameType || '')}"
        data-diameter="${this.escapeHtml(item.diameter || '')}"
      >
        ${this.escapeHtml(this.t('addToCart'))}
      </button>
    `;
  }

  renderPriceBlock(item) {
    const packSize = Math.max(1, this.readNumber(item.packSize, 1));
    const unitPrice = this.readNumber(item.price, 0) / packSize;
    const quantity = this.quantityForItem(item);
    const tier = this.applicableTier(item, quantity);

    if (this.hasTierPrices(item)) {
      const selectedUnitPrice = tier?.pricingValue || this.readNumber(item.price, 0);
      const totalPrice = selectedUnitPrice * quantity;
      const tierLabel = tier ? this.tierLabel(tier) : this.t('regularPrice');

      return `
        <div class="fa-kc__price-box fa-kc__price-box--tiered">
          <span>${this.escapeHtml(this.t('selectedUnitPrice'))}</span>
          <strong>${this.formatMoney(selectedUnitPrice)}</strong>
          <small>${this.escapeHtml(tierLabel)} | ${quantity} ${this.escapeHtml(this.t('unitPiece'))} ${this.escapeHtml(this.t('total'))} ${this.formatMoney(totalPrice)}</small>
          <small>${this.escapeHtml(this.t('regularPrice'))} ${this.formatMoney(item.price)} / ${this.escapeHtml(this.t('unitPiece'))}</small>
        </div>
      `;
    }

    if (packSize <= 1) {
      return `
        <div class="fa-kc__price-box">
          <span>${this.escapeHtml(this.t('price'))}</span>
          <strong>${this.formatMoney(item.price)}</strong>
          <small>${this.escapeHtml(this.t('perUnit'))}</small>
        </div>
      `;
    }

    return `
      <div class="fa-kc__price-box">
        <span>${this.escapeHtml(this.t('packagePrice'))}</span>
        <strong>${this.formatMoney(item.price)}</strong>
        <small>${packSize} ${this.escapeHtml(this.t('unitPiece'))} ${this.escapeHtml(this.t('inCarton'))} | ${this.escapeHtml(this.t('unitPrice'))} ${this.formatMoney(unitPrice)} / ${this.escapeHtml(this.t('unitPiece'))}</small>
      </div>
    `;
  }

  renderPriceBlockWithBenefits(item) {
    const benefits = this.renderPriceBenefits();

    return this.renderPriceBlock(item).replace(
      /<div class="fa-kc__price-box([^"]*)">([\s\S]*?)<\/div>/,
      `<div class="fa-kc__price-box$1">${benefits}<div class="fa-kc__price-values">$2</div></div>`,
    );
  }

  renderPriceBenefits() {
    const benefits = (this.config.priceBenefits || [])
      .map((benefit) => ({
        icon: String(benefit?.icon || 'none'),
        text: String(benefit?.text || '').trim(),
      }))
      .filter((benefit) => benefit.text);

    if (!benefits.length) return '';

    return `
      <ul class="fa-kc__price-benefits" aria-label="${this.escapeHtml(this.t('benefits'))}">
        ${benefits.map((benefit) => `
          <li>
            ${this.renderBenefitIcon(benefit.icon)}
            <span>${this.escapeHtml(benefit.text)}</span>
          </li>
        `).join('')}
      </ul>
    `;
  }

  renderBenefitIcon(icon) {
    const icons = {
      delivery: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 6h11v9H3z"/><path d="M14 9h4l3 3v3h-7z"/><circle cx="7" cy="18" r="2"/><circle cx="18" cy="18" r="2"/></svg>',
      payment: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18"/><path d="M7 15h5"/></svg>',
      shield: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l7 3v5c0 4.4-2.8 8.3-7 10-4.2-1.7-7-5.6-7-10V6z"/><path d="M9 12l2 2 4-5"/></svg>',
    };

    if (!icons[icon]) return '';
    return `<span class="fa-kc__price-benefit-icon fa-kc__price-benefit-icon--${this.escapeHtml(icon)}">${icons[icon]}</span>`;
  }

  renderTierControls(item) {
    const rule = this.selectedTierRule(item);
    if (!rule) return '';

    const quantity = this.quantityForItem(item);
    const activeTier = this.applicableTier(item, quantity);
    const tiers = rule.tiers.map((tier) => {
      const isActive = activeTier && tier.minQuantity === activeTier.minQuantity;

      return `
        <button
          type="button"
          class="${isActive ? 'is-active' : ''}"
          data-tier-quantity="${tier.minQuantity}"
          data-tier-variant-id="${this.escapeHtml(item.id)}"
        >
          <span>${this.escapeHtml(this.tierLabel(tier))}</span>
          <strong>${this.formatMoney(tier.pricingValue)}</strong>
        </button>
      `;
    }).join('');

    return `
      <div class="fa-kc__tier-box" data-tier-box data-variant-id="${this.escapeHtml(item.id)}">
        <label class="fa-kc__quantity">
          <span>${this.escapeHtml(this.t('quantity'))}</span>
          <input
            type="number"
            min="1"
            max="999"
            step="1"
            inputmode="numeric"
            value="${quantity}"
            data-tier-quantity-input
            data-tier-variant-id="${this.escapeHtml(item.id)}"
            aria-label="${this.escapeHtml(this.t('quantity'))}"
          >
        </label>
        <div class="fa-kc__tier-buttons" aria-label="${this.escapeHtml(this.t('tierPrices'))}">
          ${tiers}
        </div>
        <p>${this.escapeHtml(this.t('tierNote'))}</p>
      </div>
    `;
  }

  tierLabel(tier) {
    if (tier.vpeLabel) return tier.vpeLabel;
    return tier.minQuantity === 1 ? `1 ${this.t('unitPiece')}` : `${this.t('fromQuantity')} ${tier.minQuantity} ${this.t('unitPiece')}`;
  }

  packLabel(item) {
    const packSize = Math.max(1, this.readNumber(item.packSize, 1));
    return packSize === 1 ? `1 ${this.t('unitPiece')}` : `${packSize} ${this.t('unitPiece')}`;
  }

  renderNotifyForm(item, inputLength, inputWidth) {
    return `
      <form
        class="fa-kc__notify"
        data-notify-form
        data-variant-id="${this.escapeHtml(item.id)}"
        data-product-id="${this.escapeHtml(item.productId)}"
        data-inventory-item-id="${this.escapeHtml(item.inventoryItemId || '')}"
        data-sku="${this.escapeHtml(item.sku || '')}"
        data-product-title="${this.escapeHtml(item.productTitle)}"
        data-variant-title="${this.escapeHtml(item.variantTitle || '')}"
        data-product-url="${this.escapeHtml(item.url)}"
        data-input-length="${inputLength}"
        data-input-width="${inputWidth}"
        data-thickness="${item.thicknessCm}"
        data-frame-type="${this.escapeHtml(item.frameType || '')}"
        data-diameter="${this.escapeHtml(item.diameter || '')}"
      >
        <button type="button" data-notify-toggle>
          ${this.escapeHtml(this.t('notifyButton'))}
        </button>
        <div class="fa-kc__notify-fields" hidden>
          <label>
            <span>${this.escapeHtml(this.t('firstName'))}</span>
            <input type="text" name="firstName" autocomplete="given-name" required>
          </label>
          <label>
            <span>E-Mail</span>
            <input type="email" name="email" autocomplete="email" required>
          </label>
          <fieldset class="fa-kc__notify-urgency">
            <legend>${this.escapeHtml(this.t('notifyUrgencyQuestion'))}</legend>
            <label class="fa-kc__notify-option">
              <input type="radio" name="deliveryUrgency" value="urgent">
              <span>${this.escapeHtml(this.t('notifyUrgencyUrgent'))}</span>
            </label>
            <label class="fa-kc__notify-option">
              <input type="radio" name="deliveryUrgency" value="can_wait_2_3_weeks">
              <span>${this.escapeHtml(this.t('notifyUrgencyCanWait'))}</span>
            </label>
            <label class="fa-kc__notify-option">
              <input type="radio" name="deliveryUrgency" value="" checked>
              <span>${this.escapeHtml(this.t('notifyUrgencyNone'))}</span>
            </label>
          </fieldset>
          <input class="fa-kc__notify-hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">
          <button type="submit">${this.escapeHtml(this.t('notifySave'))}</button>
        </div>
      </form>
    `;
  }

  renderStockStatus(item) {
    if (this.isProductionArticle(item)) {
      return this.renderProductionAvailability();
    }

    const stockText = this.isInStock(item) && item.inventoryQuantity < 5
      ? this.t('lowStock')
      : this.isInStock(item)
        ? this.t('inStock')
        : this.t('outOfStock');

    return `
      <span class="fa-kc__stock">
        ${this.renderStockSignal(item)}
        <span>${stockText}</span>
      </span>
    `;
  }

  renderProductionAvailability() {
    return `
      <span class="fa-kc__stock">
        <span class="fa-kc__signal fa-kc__signal--production" aria-label="${this.escapeHtml(this.t('productionItem'))}"><i></i></span>
        <span>${this.escapeHtml(this.config.customAvailabilityLabel || this.t('productionItem'))}</span>
      </span>
    `;
  }

  isProductionArticle(item) {
    return item?.frameType === 'gallery_4cm' || item?.frameType === 'round';
  }

  renderAvailabilityHelp(item) {
    if (this.isProductionArticle(item)) {
      return this.renderHelp(this.t('productionHelp'));
    }

    return this.renderHelp(this.t('availabilityHelp'));
  }

  renderProductDescription(item) {
    if (item.frameType === 'round') {
      const diameter = item.diameter || item.shortSide;
      const text = this.descriptionFromTemplate('round', {
        name: this.displayTitleForItem(item),
        format: `\u00D8 ${diameter} cm`,
        durchmesser: `\u00D8 ${diameter} cm`,
        dicke: this.formatRoundDepth(item),
        profil: `${this.t('depth')} ${this.formatRoundDepth(item)}`,
        rueckseite: 'rund',
        vpe: this.packLabel(item),
      });

      return this.renderDescriptionAccordion(text);
    }

    const templateKey = Number(item.thicknessCm) === 4 ? 'gallery_4cm' : 'studio_2cm';
    const text = this.descriptionFromTemplate(templateKey, {
      name: this.displayTitleForItem(item),
      format: `${item.shortSide} x ${item.longSide} cm`,
      dicke: `${item.thicknessCm} cm`,
      profil: this.customProfileForThickness(item.thicknessCm),
      rueckseite: this.supportLabel(this.supportTypeFor(item.shortSide, item.longSide, item.thicknessCm)),
      vpe: this.packLabel(item),
    });

    return this.renderDescriptionAccordion(text);
  }

  renderCustomProductDescription(length, width, profile, reinforcement) {
    const templateKey = Number(this.state.thicknessCm) === 4 ? 'custom_4cm' : 'custom_2cm';
    const text = this.descriptionFromTemplate(templateKey, {
      name: this.t('customName'),
      format: `${length} x ${width} cm`,
      dicke: `${this.state.thicknessCm} cm`,
      profil: profile,
      rueckseite: reinforcement,
      vpe: this.packLabel({ packSize: 1 }),
    });

    return this.renderDescriptionAccordion(text);
  }

  descriptionFromTemplate(key, values) {
    const template = String(this.productDescriptionTemplates?.[key] || '').trim();
    if (!template) return '';

    return template.replace(/\{(name|format|durchmesser|dicke|profil|rueckseite|vpe)\}/g, (_match, placeholder) => (
      values[placeholder] == null ? '' : String(values[placeholder])
    )).trim();
  }

  renderDescriptionAccordion(text) {
    const paragraphs = String(text || '')
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);

    if (!paragraphs.length) return '';

    return `
      <details class="fa-kc__description">
        <summary>${this.escapeHtml(this.t('productDescription'))}</summary>
        ${paragraphs.map((paragraph) => `<p>${this.escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`).join('')}
      </details>
    `;
  }

  displayTitleForItem(item) {
    const variantTitle = String(item.variantTitle || '').trim();
    if (variantTitle && !/^default title$/i.test(variantTitle)) return variantTitle;
    return item.productTitle || this.t('canvasFrameName');
  }

  renderStockSignal(item) {
    if (this.isProductionArticle(item)) {
      return `<span class="fa-kc__signal fa-kc__signal--production" aria-label="${this.escapeHtml(this.t('productionItem'))}"><i></i></span>`;
    }

    if (!this.isInStock(item)) {
      return `<span class="fa-kc__signal fa-kc__signal--empty" aria-label="${this.escapeHtml(this.t('outOfStock'))}"><i></i></span>`;
    }

    if (item.inventoryQuantity < 5) {
      return `<span class="fa-kc__signal fa-kc__signal--orange" aria-label="${this.escapeHtml(this.t('lowStock'))}"><i></i></span>`;
    }

    if (item.inventoryQuantity <= 10) {
      return `<span class="fa-kc__signal fa-kc__signal--green" aria-label="${this.escapeHtml(this.t('inStock'))}"><i></i></span>`;
    }

    return `<span class="fa-kc__signal fa-kc__signal--green-double" aria-label="${this.escapeHtml(this.t('veryAvailable'))}"><i></i><i></i></span>`;
  }

  renderDeliveryStatus(item) {
    if (!this.isInStock(item)) return this.t('availableByStock');

    if (!this.config.deliveryEstimateUrl) return this.t('deliveryUnavailable');
    this.ensureDeliveryEstimate(item);
    if (item.deliveryText) return this.escapeHtml(item.deliveryText);
    if (item.deliveryError) return this.t('deliveryReload');

    return this.t('customPriceLoading');
  }

  async ensureDeliveryEstimate(item) {
    const endpoint = this.config.deliveryEstimateUrl;
    if (!endpoint || item.deliveryRequested || item.deliveryLoading || !this.isInStock(item)) return;

    item.deliveryRequested = true;
    item.deliveryLoading = true;
    item.deliveryError = false;
    const key = String(item.id || item.sku || `${item.thicknessCm}:${item.shortSide}x${item.longSide}`);
    const requestId = (this.deliveryRequestIds[key] || 0) + 1;
    this.deliveryRequestIds[key] = requestId;

    try {
      const response = await this.fetchWithTimeout(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          locale: this.locale,
          sku: item.sku || '',
          thicknessCm: item.thicknessCm,
          productGroup: item.frameType === 'round' ? 'round' : '',
          inventoryQuantity: item.inventoryQuantity,
        }),
      }, 10000);

      const payload = await response.json().catch(() => ({}));
      if (this.deliveryRequestIds[key] !== requestId) return;
      if (!response.ok || !payload.ok) throw new Error(payload.message || this.t('deliveryFailed'));

      const estimate = payload.estimate || {};
      item.deliveryText = String(estimate.deliveryText || '').trim() || this.t('deliveryUnavailable');
      item.deliveryError = false;
    } catch {
      if (this.deliveryRequestIds[key] !== requestId) return;
      item.deliveryText = '';
      item.deliveryError = true;
    } finally {
      if (this.deliveryRequestIds[key] === requestId) {
        item.deliveryLoading = false;
        this.render();
        this.updateMobileDock();
      }
    }
  }

  renderNoMatch(length, width) {
    if (this.isRoundMode()) {
      this.result.innerHTML = `
        <div class="fa-kc__empty fa-kc__empty--request">
          ${this.renderPreview(length, length, this.state.thicknessCm)}
          <p class="fa-kc__status fa-kc__status--notice">Kein direkter Treffer</p>
          <h2>\u00D8 ${this.escapeHtml(length)} cm</h2>
          <p>${this.escapeHtml(this.t('roundNoMatch'))}</p>
        </div>
      `;
      this.updateMobileDock();
      return;
    }

    const customHint = this.state.includeCustom
      ? this.t('customNoMatch')
      : this.t('standardNoMatch');
    const customPanel = this.state.includeCustom ? this.renderCustomPanel(length, width) : '';

    this.result.innerHTML = `
      <div class="fa-kc__empty fa-kc__empty--request">
        ${this.renderPreview(length, width, this.state.thicknessCm)}
        <p class="fa-kc__status fa-kc__status--notice">Kein direkter Treffer</p>
        <h2>${this.escapeHtml(length)} x ${this.escapeHtml(width)} cm - ${this.state.thicknessCm} cm ${this.escapeHtml(this.t('thickness'))}</h2>
        <p>${customHint}</p>
        ${customPanel}
      </div>
    `;

    if (this.state.includeCustom) this.fetchCustomQuote(length, width);
    this.updateMobileDock();
  }

  renderCustomSelection(length, width) {
    const calculable = this.isCalculableCustomSize(length, width);
    this.lastCustomQuote = null;
    this.result.innerHTML = `
      <div class="fa-kc__empty fa-kc__empty--request">
        ${this.renderPreview(length, width, this.state.thicknessCm)}
        <p class="fa-kc__status fa-kc__status--notice">${this.escapeHtml(this.t('specialProduction'))}</p>
        <h2>${this.escapeHtml(length)} x ${this.escapeHtml(width)} cm - ${this.state.thicknessCm} cm ${this.escapeHtml(this.t('thickness'))}</h2>
        <p>${this.escapeHtml(this.t(calculable ? 'customNoMatch' : 'sizeRequestReason'))}</p>
        ${calculable ? this.renderCustomPanel(length, width) : this.renderSizeRequestButton()}
      </div>
    `;

    if (calculable) this.fetchCustomQuote(length, width);
    else this.customQuoteRequestId += 1;
    this.updateMobileDock();
  }

  renderSizeRequestButton() {
    return `
      <button type="button" class="fa-kc__size-request-open" data-size-request-open>
        <span>${this.escapeHtml(this.t('sizeRequestOpen'))}</span>
        <span class="fa-kc__size-request-arrow" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" focusable="false"><path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>
        </span>
      </button>
    `;
  }

  customProductImageForFrameType() {
    const images = this.config.customProductImages || {};
    return (
      images[this.state.frameType] ||
      this.config.customProductImageUrl ||
      this.config.labelImage ||
      ''
    );
  }

  renderCustomPanel(length, width) {
    const imageUrl = this.customProductImageForFrameType();
    const image = imageUrl
      ? `<img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(this.t('customName'))}" loading="lazy">`
      : '<div class="fa-kc__image-placeholder" aria-hidden="true"></div>';
    const profile = this.customProfileForThickness(this.state.thicknessCm);
    const reinforcement = this.supportLabel(this.supportTypeFor(length, width, this.state.thicknessCm));

    return `
      <article class="fa-kc__match fa-kc__match--custom fa-kc__custom-order" data-custom-order>
        <button
          type="button"
          class="fa-kc__match-media"
          data-preview-image="${this.escapeHtml(imageUrl)}"
          data-preview-title="${this.escapeHtml(this.t('customName'))}"
          aria-label="${this.escapeHtml(this.t('imageZoom'))}"
        >
          ${image}
        </button>
        <div class="fa-kc__match-body">
          <h3>${this.escapeHtml(this.t('customName'))}</h3>
          <p class="fa-kc__match-subtitle">${length} x ${width} cm</p>
          <div class="fa-kc__match-mobile-meta">
            <span>${length} x ${width} cm</span>
            <span>${this.state.thicknessCm} cm ${this.escapeHtml(this.t('thickness'))}</span>
          </div>
          <dl>
            <div><dt>${this.escapeHtml(this.t('customFormatLabel'))}</dt><dd>${length} x ${width} cm</dd></div>
            <div><dt>${this.escapeHtml(this.t('thickness'))}</dt><dd>${this.state.thicknessCm} cm</dd></div>
            <div><dt>${this.escapeHtml(this.t('customProfileLabel'))}</dt><dd data-custom-profile>${this.escapeHtml(profile)}</dd></div>
            <div><dt>${this.escapeHtml(this.t('customReinforcementLabel'))}</dt><dd data-custom-reinforcement>${this.escapeHtml(reinforcement)}</dd></div>
            <div class="fa-kc__stock-cell"><dt>${this.escapeHtml(this.t('availability'))} ${this.renderHelp(this.t('productionHelp'))}</dt><dd>${this.renderProductionAvailability()}</dd></div>
            <div class="fa-kc__delivery-cell"><dt>${this.escapeHtml(this.t('deliveryDate'))} ${this.renderHelp(this.t('deliveryHelp'))}</dt><dd data-custom-delivery>${this.escapeHtml(this.t('customPriceLoading'))}</dd></div>
          </dl>
          ${this.renderCustomProductDescription(length, width, profile, reinforcement)}
          <div class="fa-kc__price-box fa-kc__price-box--custom" data-custom-price>
            ${this.renderPriceBenefits()}
            <div class="fa-kc__price-values">
              <span>${this.escapeHtml(this.t('price'))}</span>
              <strong>${this.escapeHtml(this.t('customPriceLoading'))}</strong>
            </div>
          </div>
          <div class="fa-kc__match-actions">
            <button type="button" disabled data-custom-draft-button data-length="${length}" data-width="${width}" data-thickness="${this.state.thicknessCm}">
              ${this.escapeHtml(this.t('addToCart'))}
            </button>
          </div>
          ${this.renderShareButton(imageUrl)}
        </div>
      </article>
    `;
  }

  customProfileForThickness(thicknessCm) {
    return Number(thicknessCm) === 4 ? '50 x 40' : '50 x 19';
  }

  async fetchCustomQuote(length, width) {
    const target = this.result?.querySelector('[data-custom-price]');
    const submitButton = this.result?.querySelector('[data-custom-draft-button]');
    const endpoint = this.config.customPriceUrl;
    const requestId = (this.customQuoteRequestId += 1);

    if (!target || !submitButton) return;

    if (!endpoint) {
      target.textContent = this.t('customPriceMissing');
      return;
    }

    target.innerHTML = `
      ${this.renderPriceBenefits()}
      <div class="fa-kc__price-values">
        <span>${this.escapeHtml(this.t('price'))}</span>
        <strong>${this.escapeHtml(this.t('customPriceLoading'))}</strong>
      </div>
    `;
    submitButton.disabled = true;

    try {
      const response = await this.fetchWithTimeout(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          locale: this.locale,
          length,
          width,
          thicknessCm: this.state.thicknessCm,
        }),
      }, 15000);

      const payload = await response.json().catch(() => ({}));
      if (requestId !== this.customQuoteRequestId) return;
      if (response.status === 404) {
        throw new Error(this.t('customPriceMissing'));
      }
      if (!response.ok) throw new Error(payload.message || this.t('customPriceFailed'));

      const quote = payload.quote || {};
      this.lastCustomQuote = {
        length,
        width,
        thicknessCm: this.state.thicknessCm,
        formattedPrice: String(quote.formattedPrice || '').trim(),
      };
      const profileTarget = this.result?.querySelector('[data-custom-profile]');
      const reinforcementTarget = this.result?.querySelector('[data-custom-reinforcement]');
      const deliveryTarget = this.result?.querySelector('[data-custom-delivery]');
      if (profileTarget) profileTarget.textContent = quote.profile || this.customProfileForThickness(this.state.thicknessCm);
      if (reinforcementTarget) reinforcementTarget.textContent = quote.reinforcement || this.supportLabel(this.supportTypeFor(length, width, this.state.thicknessCm));
      if (deliveryTarget) deliveryTarget.textContent = quote.deliveryText || 'ca. 1-2 Wochen';
      target.innerHTML = `
        ${this.renderPriceBenefits()}
        <div class="fa-kc__price-values">
          <span>${this.escapeHtml(this.t('price'))}</span>
          <strong>${this.escapeHtml(quote.formattedPrice || '')}</strong>
          <small>${this.escapeHtml(this.t('customPriceSmall'))}</small>
        </div>
      `;
      submitButton.disabled = false;
      this.updateMobileDock();
    } catch (error) {
      if (requestId !== this.customQuoteRequestId) return;
      target.innerHTML = this.renderCustomQuoteError(error, length, width);
      submitButton.disabled = true;
    }
  }

  renderCustomQuoteError(error, length, width) {
    const isTimeout = error?.code === 'REQUEST_TIMEOUT';
    const message = isTimeout
      ? this.t('customPriceTimeout')
      : (error?.message || this.t('customPriceFailed'));

    return `
      <div class="fa-kc__custom-error">
        <strong>${this.escapeHtml(isTimeout ? this.t('customPriceRetry') : this.t('customPriceTitle'))}</strong>
        <span>${this.escapeHtml(message)}</span>
        ${this.renderSizeRequestButton()}
        <span>${this.escapeHtml(this.t('customRetryHint'))}</span>
        <span class="fa-kc__custom-error-actions">
          <button type="button" data-custom-quote-retry data-length="${this.escapeHtml(length)}" data-width="${this.escapeHtml(width)}">${this.escapeHtml(this.t('customPriceRetry'))}</button>
          <button type="button" data-custom-quote-reload>${this.escapeHtml(this.t('pageReload'))}</button>
        </span>
      </div>
    `;
  }

  handleQuickSizeClick(event) {
    const button = event.target.closest('button[data-length][data-width]');
    if (!button) return;

    this.state.length = this.readNumber(button.dataset.length, this.state.length);
    this.state.width = this.readNumber(button.dataset.width, this.state.width);
    this.applyModeConstraints();
    this.render();
    this.updateMobileDock();
    this.handleMobileInputProgress();
    this.scheduleMobileResultReveal('choice');
  }

  handleQuantityChange(event) {
    const input = event.target.closest('[data-tier-quantity-input]');
    if (!input) return;

    const item = this.items.find((candidate) => String(candidate.id) === String(input.dataset.tierVariantId));
    if (!item) return;

    this.setQuantityForItem(item, input.value);
    this.render();
    this.updateMobileDock();
  }

  async handleResultClick(event) {
    const imageButton = event.target.closest('button[data-preview-image]');
    if (imageButton) {
      this.openImageModal(imageButton.dataset.previewImage, imageButton.dataset.previewTitle);
      return;
    }

    const tierButton = event.target.closest('button[data-tier-quantity]');
    if (tierButton) {
      const item = this.items.find((candidate) => String(candidate.id) === String(tierButton.dataset.tierVariantId));
      if (item) {
        this.setQuantityForItem(item, tierButton.dataset.tierQuantity);
        this.render();
        this.updateMobileDock();
      }
      return;
    }

    const notifyToggle = event.target.closest('[data-notify-toggle]');
    if (notifyToggle) {
      const fields = notifyToggle.closest('[data-notify-form]')?.querySelector('.fa-kc__notify-fields');
      if (fields) fields.hidden = !fields.hidden;
      return;
    }

    const customButton = event.target.closest('button[data-custom-draft-button]');
    if (customButton && !customButton.disabled) {
      await this.handleCustomCartClick(customButton);
      return;
    }

    const shareOption = event.target.closest('[data-share-option]');
    if (shareOption) {
      await this.handleShareOptionClick(shareOption);
      return;
    }

    const shareButton = event.target.closest('button[data-share-config]');
    if (shareButton) {
      this.toggleShareWidget(shareButton);
      return;
    }

    if (!event.target.closest('[data-share-widget]')) {
      this.closeShareWidgets();
    }

    const customRetry = event.target.closest('[data-custom-quote-retry]');
    if (customRetry) {
      this.fetchCustomQuote(
        this.readNumber(customRetry.dataset.length, this.state.length),
        this.readNumber(customRetry.dataset.width, this.state.width),
      );
      return;
    }

    if (event.target.closest('[data-custom-quote-reload]')) {
      window.location.reload();
      return;
    }

    const button = event.target.closest('button[data-add-variant]');
    if (!button || button.disabled) return;

    const variantId = button.dataset.addVariant;
    const item = this.items.find((candidate) => String(candidate.id) === String(variantId));
    if (!item) return;
    const quantityInput = button.closest('.fa-kc__match')?.querySelector('[data-tier-quantity-input]');
    const quantity = this.hasTierPrices(item)
      ? this.clamp(this.readNumber(quantityInput?.value, this.quantityForItem(item)), 1, 999)
      : 1;
    this.setQuantityForItem(item, quantity);

    button.disabled = true;
    button.textContent = this.t('addingToCart');

    try {
      const response = await this.fetchWithTimeout(this.config.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity,
          properties: this.cartPropertiesForItem(item, button, quantity),
        }),
      }, 12000);

      if (!response.ok) {
        throw new Error(await this.readCartError(response));
      }

      const cart = await this.fetchWithTimeout('/cart.js', {}, 8000).then((cartResponse) => cartResponse.json()).catch(() => null);
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: this.dataset.sectionId,
            data: {
              source: 'keilrahmen-configurator',
              variantId,
              itemCount: cart?.item_count,
            },
          },
        }),
      );
      document.querySelector('cart-drawer-component')?.open?.();

      button.hidden = true;
      this.showInlineStatus('success', this.t('cartSuccess'), {
        target: button.closest('.fa-kc__match-actions'),
        actions: true,
      });
    } catch (error) {
      button.disabled = false;
      button.textContent = this.t('addToCart');
      this.showInlineStatus('error', error.message || this.t('cartError'), {
        target: button.closest('.fa-kc__match-actions'),
      });
    }
  }

  async handleNotifySubmit(event) {
    const form = event.target.closest('[data-notify-form]');
    if (!form) return;

    event.preventDefault();

    const endpoint = this.config.notifyAppUrl;
    if (!endpoint) {
      this.showInlineStatus('error', this.t('notifyAppMissing'));
      return;
    }

    const submitButton = form.querySelector('button[type="submit"]');
    const formData = new FormData(form);
    const deliveryUrgency = formData.get('deliveryUrgency');
    const payload = {
      firstName: String(formData.get('firstName') || '').trim(),
      email: String(formData.get('email') || '').trim(),
      website: String(formData.get('website') || '').trim(),
      deliveryUrgency: ['urgent', 'can_wait_2_3_weeks'].includes(deliveryUrgency) ? deliveryUrgency : null,
      locale: this.normalizeLocale(this.locale || this.config.locale),
      product: {
        id: form.dataset.productId || '',
        title: form.dataset.productTitle || '',
        url: form.dataset.productUrl || '',
      },
      variant: {
        id: form.dataset.variantId || '',
        title: form.dataset.variantTitle || '',
        sku: form.dataset.sku || '',
        inventoryItemId: form.dataset.inventoryItemId || '',
      },
      selection: {
        length: this.readNumber(form.dataset.inputLength, 0),
        width: this.readNumber(form.dataset.inputWidth, 0),
        thicknessCm: this.readNumber(form.dataset.thickness, 0),
        frameType: form.dataset.frameType || '',
        diameter: this.readNumber(form.dataset.diameter, 0),
      },
      source: 'keilrahmen-configurator',
    };

    if (!payload.firstName || !payload.email) {
      this.showInlineStatus('error', this.t('notifyMissing'));
      return;
    }

    submitButton.disabled = true;
    submitButton.textContent = this.t('notifySaving');

    try {
      const response = await this.fetchWithTimeout(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      }, 12000);

      const responsePayload = await response.json().catch(() => ({}));
      if (!response.ok || responsePayload.ok === false) throw new Error(responsePayload.message || this.t('notifySaveFailed'));

      form.reset();
      form.querySelector('.fa-kc__notify-fields')?.setAttribute('hidden', '');
      submitButton.textContent = this.t('notifySaved');
      this.showInlineStatus('success', this.t('backInStockSuccess'));
    } catch (error) {
      submitButton.disabled = false;
      submitButton.textContent = this.t('notifySave');
      this.showInlineStatus('error', error.message || this.t('notifySaveFailed'));
    }
  }

  cartPropertiesForItem(item, button, quantity) {
    if (item.frameType === 'round') {
      const diameter = item.diameter || item.shortSide || this.readNumber(button.dataset.inputLength, 0);
      return {
        _konfigurator: 'keilrahmen',
        _keilrahmen_form: 'Rund',
        _durchmesser: `\u00D8 ${diameter} cm`,
        _profil: `${this.t('depth')} ${this.formatRoundDepth(item)}`,
        _eingabeformat: `\u00D8 ${diameter} cm`,
        _normalisiertes_format: `\u00D8 ${diameter} cm`,
        _menge_konfigurator: String(quantity),
        _sku: item.sku || '',
      };
    }

    return {
      _konfigurator: 'keilrahmen',
      _keilrahmen_dicke: `${item.thicknessCm} cm`,
      _eingabeformat: `${button.dataset.inputLength}x${button.dataset.inputWidth} cm`,
      _normalisiertes_format: `${item.shortSide}x${item.longSide} cm`,
      _menge_konfigurator: String(quantity),
      _sku: item.sku || '',
    };
  }

  async handleCustomCartClick(button) {
    const endpoint = this.config.customCartUrl || this.config.customDraftUrl;
    if (!endpoint) {
      this.showInlineStatus('error', this.t('customFeatureMissing'));
      return;
    }

      const payload = {
        locale: this.locale,
        length: this.readNumber(button.dataset.length, 0),
        width: this.readNumber(button.dataset.width, 0),
        thicknessCm: this.readNumber(button.dataset.thickness, 0),
        imageUrl: this.customProductImageForFrameType(),
      };

    button.disabled = true;
    button.textContent = this.t('addingToCart');

    try {
      const response = await this.fetchWithTimeout(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      }, 15000);

      const responsePayload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(responsePayload.message || this.t('customCreateFailed'));

      const cartItem = responsePayload.cartItem || {};
      const quote = responsePayload.quote || {};
      const variantId = Number(cartItem.variantId || 0);
      if (!variantId) throw new Error(this.t('customVariantMissing'));

      const addResponse = await this.fetchWithTimeout(this.config.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            _konfigurator: 'keilrahmen-sonderanfertigung',
            _keilrahmen_dicke: `${payload.thicknessCm} cm`,
            _eingabeformat: `${payload.length}x${payload.width} cm`,
            _profil: quote.profile || '',
            _aussteifung: quote.reinforcement || '',
            _liefertermin: quote.deliveryText || '',
            _sku: cartItem.sku || '',
            _preview_image: payload.imageUrl || cartItem.imageUrl || '',
          },
        }),
      }, 12000);

      if (!addResponse.ok) {
        throw new Error(await this.readCartError(addResponse));
      }

      const cart = await this.fetchWithTimeout('/cart.js', {}, 8000).then((cartResponse) => cartResponse.json()).catch(() => null);
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: this.dataset.sectionId,
            data: {
              source: 'keilrahmen-configurator-custom',
              variantId,
              itemCount: cart?.item_count,
            },
          },
        }),
      );
      document.querySelector('cart-drawer-component')?.open?.();

      button.hidden = true;
      this.showInlineStatus('success', this.t('customCartSuccess'), {
        target: button.closest('[data-custom-order]'),
        actions: true,
      });
    } catch (error) {
      button.disabled = false;
      button.textContent = this.t('addToCart');
      this.showInlineStatus('error', error.message || this.t('customCartError'), {
        target: button.closest('[data-custom-order]'),
      });
    }
  }

  isInStock(item) {
    return Boolean(item.available) && item.inventoryQuantity > 0;
  }

  renderShareButton(imageUrl = '') {
    const shareImage = this.absoluteShareImageUrl(imageUrl);
    const imageAttribute = shareImage ? ` data-share-image="${this.escapeHtml(shareImage)}"` : '';
    return `
      <div class="fa-kc__share" data-share-widget${imageAttribute}>
        <button type="button" class="fa-kc__share-trigger" data-share-config aria-label="${this.escapeHtml(this.t('shareTitle'))}" aria-expanded="false" data-share-tooltip="${this.escapeHtml(this.t('share'))}">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M12 4v11"></path>
            <path d="m7 9 5-5 5 5"></path>
            <path d="M5 13v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5"></path>
          </svg>
        </button>
        <div class="fa-kc__share-popover" data-share-popover hidden>
          <strong>${this.escapeHtml(this.t('shareTitle'))}</strong>
          <button type="button" data-share-option="whatsapp">${this.escapeHtml(this.t('shareWhatsApp'))}</button>
          <button type="button" data-share-option="email">${this.escapeHtml(this.t('shareEmail'))}</button>
          <button type="button" data-share-option="copy">${this.escapeHtml(this.t('shareCopy'))}</button>
          <div class="fa-kc__share-status" data-share-status hidden></div>
        </div>
      </div>
    `;
  }

  applySharedUrlState() {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      return false;
    }

    const searchPrefill = params.get('source') === 'search'
      && params.get('include_custom') === '1'
      && params.has('length')
      && params.has('width');
    const type = String(params.get('kc_type') || (searchPrefill ? 'custom_2cm' : '')).trim();
    if (!type) return false;

    const length = this.readNumber(params.get('kc_length') || params.get('length'), NaN);
    const width = this.readNumber(params.get('kc_width') || params.get('width'), NaN);
    const diameter = this.readNumber(params.get('kc_diameter'), NaN);
    const isCustom = params.get('kc_custom') === '1' || searchPrefill;

    if (type === 'round') {
      this.state.frameType = 'round';
      this.state.thicknessCm = 4;
      this.state.includeCustom = false;
      if (Number.isFinite(diameter) && diameter > 0) {
        this.state.length = diameter;
        this.state.width = diameter;
      }
      return true;
    }

    if (type === 'gallery_4cm' || type === 'custom_4cm') {
      this.state.frameType = 'gallery_4cm';
      this.state.thicknessCm = 4;
      this.state.includeCustom = isCustom || type === 'custom_4cm';
    } else if (type === 'studio_2cm' || type === 'custom_2cm') {
      this.state.frameType = 'studio_2cm';
      this.state.thicknessCm = 2;
      this.state.includeCustom = isCustom || type === 'custom_2cm';
    } else {
      return false;
    }

    const sharedMax = this.state.includeCustom && this.state.thicknessCm === 4 ? 10000 : 195;
    if (Number.isInteger(length) && length >= 15 && length <= sharedMax) this.state.length = length;
    if (Number.isInteger(width) && width >= 15 && width <= sharedMax) this.state.width = width;
    return true;
  }

  absoluteShareImageUrl(imageUrl) {
    const value = String(imageUrl || '').trim();
    if (!value) return '';
    try {
      return new URL(value, window.location.origin).toString();
    } catch {
      return '';
    }
  }

  shareConfigUrl(widget = null) {
    const targetUrl = this.configuratorTargetUrl();
    if (!this.config.shareUrl) return targetUrl.toString();

    const url = new URL(this.config.shareUrl, window.location.origin);
    [...url.searchParams.keys()].forEach((key) => {
      if (key.startsWith('kc_')) url.searchParams.delete(key);
    });

    Object.entries(this.shareParams()).forEach(([key, value]) => url.searchParams.set(key, value));
    url.searchParams.set('target', targetUrl.toString());
    const productImage = this.shareProductImageUrl(widget);
    if (productImage) url.searchParams.set('kc_product_image', productImage);
    if (this.config.labelImage) url.searchParams.set('kc_label_image', this.config.labelImage);
    if (!this.isRoundMode()) url.searchParams.set('kc_support', this.supportTypeFor(this.state.length, this.state.width, this.state.thicknessCm));

    return url.toString();
  }

  shareParams() {
    if (this.isRoundMode()) {
      return {
        kc_type: 'round',
        kc_diameter: String(this.state.length),
      };
    }

    const customPrefix = this.state.includeCustom ? 'custom' : (this.state.thicknessCm === 4 ? 'gallery' : 'studio');
    const params = {
      kc_type: `${customPrefix}_${this.state.thicknessCm}cm`,
      kc_length: String(this.state.length),
      kc_width: String(this.state.width),
    };
    if (this.state.includeCustom) params.kc_custom = '1';
    return params;
  }

  shareProductImageUrl(widget = null) {
    return this.absoluteShareImageUrl(widget?.dataset?.shareImage || '');
  }

  shareImageUrl(widget = null) {
    const productImage = this.shareProductImageUrl(widget);
    if (productImage) return productImage;
    if (!this.config.shareUrl) return '';

    const url = new URL(this.config.shareUrl, window.location.origin);
    url.pathname = url.pathname.endsWith('/share')
      ? url.pathname.replace(/\/share$/, '/share-image.png')
      : `${url.pathname.replace(/\/$/, '')}/share-image.png`;
    [...url.searchParams.keys()].forEach((key) => {
      if (key.startsWith('kc_') || key === 'target') url.searchParams.delete(key);
    });
    Object.entries(this.shareParams()).forEach(([key, value]) => url.searchParams.set(key, value));
    if (this.config.labelImage) url.searchParams.set('kc_label_image', this.config.labelImage);
    if (!this.isRoundMode()) url.searchParams.set('kc_support', this.supportTypeFor(this.state.length, this.state.width, this.state.thicknessCm));
    return url.toString();
  }

  configuratorTargetUrl() {
    const url = new URL(window.location.href);
    [...url.searchParams.keys()].forEach((key) => {
      if (key.startsWith('kc_')) url.searchParams.delete(key);
    });

    Object.entries(this.shareParams()).forEach(([key, value]) => url.searchParams.set(key, value));

    return url;
  }

  shareConfigText() {
    if (this.isRoundMode()) return `FLEURY runder Keilrahmen \u00D8 ${this.state.length} cm`;
    const prefix = this.state.includeCustom ? this.t('shareCustomTitle') : this.t('shareStandardTitle');
    return `${prefix}: ${this.state.length} x ${this.state.width} cm, ${this.state.thicknessCm} cm ${this.t('thickness')}`;
  }

  toggleShareWidget(button) {
    const widget = button.closest('[data-share-widget]');
    const popover = widget?.querySelector('[data-share-popover]');
    if (!widget || !popover) return;

    const willOpen = popover.hidden;
    this.closeShareWidgets(widget);
    popover.hidden = !willOpen;
    button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    this.setShareStatus(widget, '', '');
  }

  closeShareWidgets(except = null) {
    this.querySelectorAll('[data-share-widget]').forEach((widget) => {
      if (widget === except) return;
      const popover = widget.querySelector('[data-share-popover]');
      const trigger = widget.querySelector('[data-share-config]');
      if (popover) popover.hidden = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  async handleShareOptionClick(optionButton) {
    const widget = optionButton.closest('[data-share-widget]');
    const action = String(optionButton.dataset.shareOption || '');
    if (!widget || !action) return;

    const buttons = widget.querySelectorAll('[data-share-option]');
    buttons.forEach((button) => { button.disabled = true; });
    this.setShareStatus(widget, this.t('sharePreparing'), 'loading');

    try {
      const url = await this.resolvedShareConfigUrl(widget);
      const text = this.shareConfigText();

      if (action === 'whatsapp') {
        const message = `${text}\n${url}`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
        const popup = window.open(whatsappUrl, '_blank', 'noopener');
        if (!popup) window.location.href = whatsappUrl;
        this.setShareStatus(widget, this.t('shareWhatsAppOpening'), 'success');
      } else if (action === 'email') {
        const subject = this.t('shareSubject');
        const body = `${text}\n\n${url}`;
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        this.setShareStatus(widget, this.t('shareEmailPreparing'), 'success');
      } else {
        await this.copyShareUrl(url, widget);
      }
    } catch {
      this.setShareStatus(widget, this.t('sharePrepareFailed'), 'error');
    } finally {
      window.setTimeout(() => {
        buttons.forEach((button) => {
          if (button.isConnected) button.disabled = false;
        });
      }, 600);
    }
  }

  async copyShareUrl(url, widget) {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(url);
      this.setShareStatus(widget, this.t('shareCopied'), 'success');
    } catch {
      this.setShareStatus(widget, `
        <span>Link manuell kopieren:</span>
        <input type="text" readonly value="${this.escapeHtml(url)}" onfocus="this.select()">
      `, 'manual', true);
    }
  }

  setShareStatus(widget, message, type, isHtml = false) {
    const status = widget?.querySelector('[data-share-status]');
    if (!status) return;
    status.hidden = !message;
    status.className = `fa-kc__share-status ${type ? `fa-kc__share-status--${type}` : ''}`;
    if (isHtml) {
      status.innerHTML = message;
    } else {
      status.textContent = message;
    }
  }

  async resolvedShareConfigUrl(widget = null) {
    const shareUrl = this.shareConfigUrl(widget);
    const targetUrl = this.configuratorTargetUrl().toString();
    const shortlinkEndpoint = this.config.shareShortlinkUrl ? new URL(this.config.shareShortlinkUrl, window.location.origin) : null;
    if (shortlinkEndpoint) {
      Object.entries(this.shareParams()).forEach(([key, value]) => shortlinkEndpoint.searchParams.set(key, value));
    }
    const shortlinkUrl = shortlinkEndpoint ? shortlinkEndpoint.toString() : '';
    if (shortlinkUrl) {
      try {
        const response = await this.fetchWithTimeout(shortlinkUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            locale: this.locale,
            targetUrl,
            shareUrl,
            imageUrl: this.shareImageUrl(widget),
            title: this.shareConfigText(),
            params: this.shareParams(),
          }),
        }, 7000);
        const payload = await response.json().catch(() => ({}));
        if (response.ok && payload.ok && typeof payload.url === 'string' && payload.url) {
          return payload.url;
        }
      } catch {
        // Keep the normal share link active when Switchy or the short-link endpoint is unavailable.
      }
    }

    if (!this.config.shareUrl || shareUrl === targetUrl) return targetUrl;

    try {
      const response = await this.fetchWithTimeout(shareUrl, {
        method: 'GET',
        headers: { Accept: 'text/html' },
        cache: 'no-store',
        redirect: 'manual',
      }, 3500);
      if (response.ok) return shareUrl;
    } catch {
      // Keep the direct configurator link when the share endpoint is unavailable.
    }

    return targetUrl;
  }

  supportTypeFor(length, width, thicknessCm) {
    const key = this.supportKey(thicknessCm, length, width);
    const override = this.supportOverrides?.[key];
    if (Math.max(length, width) <= 195 && ['none', 'querleiste', 'kreuz', 'doppelkreuz'].includes(override)) return override;

    const longSide = Math.max(length, width);
    const shortSide = Math.min(length, width);

    const sidesAtLeast50 = (length >= 50 ? 1 : 0) + (width >= 50 ? 1 : 0);
    if (longSide > 250) return 'dreifachkreuz';
    if (longSide > 195) return 'doppelkreuz';
    if (sidesAtLeast50 >= 2) return 'kreuz';
    if (sidesAtLeast50 === 1 || (longSide >= 120 && shortSide >= 20 && shortSide <= 40)) return 'querleiste';
    return 'none';
  }

  supportKey(thicknessCm, length, width) {
    return `${thicknessCm}:${Math.min(length, width)}x${Math.max(length, width)}`;
  }

  supportLabel(type) {
    return {
      none: this.t('supportNone'),
      querleiste: this.t('supportBar'),
      kreuz: this.t('supportCross'),
      doppelkreuz: this.t('supportDoubleCross'),
      dreifachkreuz: this.t('supportTripleCross'),
    }[type] || this.t('supportAuto');
  }

  rangeValues(min, max, step) {
    const values = [];
    for (let value = min; value <= max; value += step) values.push(value);
    if (!values.includes(max)) values.push(max);
    return values;
  }

  customTickValues() {
    return this.rangeValues(15, this.customInputMax(), 10);
  }

  customInputMax() {
    return this.state.thicknessCm === 4 ? 300 : 195;
  }

  activateCustomForLargeSize(value) {
    if (this.state.thicknessCm !== 4 || this.isRoundMode() || this.state.includeCustom || value <= 195) return false;
    this.state.includeCustom = true;
    if (this.includeCustomInput) this.includeCustomInput.checked = true;
    return true;
  }

  isCalculableCustomSize(length, width) {
    return Math.min(length, width) >= 15 && Math.min(length, width) <= 195
      && Math.max(length, width) <= this.customInputMax();
  }

  addSizeRequestListeners() {
    if (this.sizeRequestClickHandler) return;
    this.sizeRequestClickHandler = (event) => {
      const panel = this.querySelector('[data-size-request-panel]');
      if (!panel) return;
      if (event.target.closest('[data-size-request-open]')) {
        panel.hidden = false;
        const row = panel.querySelector('[data-size-request-row]');
        if (panel.querySelectorAll('[data-size-request-row]').length === 1) {
          row.querySelector('[data-size-length]').value = String(this.state.length);
          row.querySelector('[data-size-width]').value = String(this.state.width);
        }
        panel.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'nearest' });
        panel.querySelector('[data-size-length]')?.focus();
      }
      if (event.target.closest('[data-size-request-add]')) {
        const rows = panel.querySelector('[data-size-request-rows]');
        if (rows.children.length >= 50) return;
        const next = rows.firstElementChild.cloneNode(true);
        next.querySelectorAll('input').forEach((input) => { input.value = input.hasAttribute('data-size-quantity') ? '1' : ''; });
        next.querySelector('[data-size-request-remove]').hidden = false;
        rows.append(next);
        next.querySelector('input').focus();
      }
      if (event.target.closest('[data-size-request-remove]')) {
        const row = event.target.closest('[data-size-request-row]');
        if (row?.parentElement.children.length > 1) row.remove();
      }
    };
    this.sizeRequestSubmitHandler = (event) => {
      const form = event.target.closest('[data-size-request-form]');
      if (!form) return;
      const rows = [...form.querySelectorAll('[data-size-request-row]')];
      form.querySelector('[data-size-request-body]').value = `${this.t('sizeRequestTitle')} (4 cm)\n` + rows.map((row, index) =>
        `${index + 1}. ${row.querySelector('[data-size-length]').value} x ${row.querySelector('[data-size-width]').value} cm; ${this.t('sizeRequestQuantity')}: ${row.querySelector('[data-size-quantity]').value}`,
      ).join('\n');
    };
    this.addEventListener('click', this.sizeRequestClickHandler);
    this.addEventListener('submit', this.sizeRequestSubmitHandler);
  }

  addBusinessDays(date, days) {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let remaining = days;

    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) remaining -= 1;
    }

    return result;
  }

  formatDate(date) {
    return new Intl.DateTimeFormat(this.localeTag(), {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    }).format(date);
  }

  readNumber(value, fallback) {
    const number = Number.parseInt(value, 10);
    return Number.isFinite(number) ? number : fallback;
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  nearestCombo(length, width, combos) {
    if (!combos.length) return null;

    return combos.reduce((nearest, combo) => {
      const currentDistance = Math.abs(combo.length - length) + Math.abs(combo.width - width);
      const nearestDistance = Math.abs(nearest.length - length) + Math.abs(nearest.width - width);
      return currentDistance < nearestDistance ? combo : nearest;
    }, combos[0]);
  }

  nearestAllowedValue(value, allowedValues) {
    return allowedValues.reduce((nearest, candidate) => {
      const currentDistance = Math.abs(candidate - value);
      const nearestDistance = Math.abs(nearest - value);
      return currentDistance < nearestDistance ? candidate : nearest;
    }, allowedValues[0]);
  }

  indexForAllowedValue(value, allowedValues) {
    return Math.max(0, allowedValues.indexOf(this.nearestAllowedValue(value, allowedValues)));
  }

  async readCartError(response) {
    try {
      const payload = await response.json();
      return payload.description || payload.message || this.t('cartError');
    } catch {
      return this.t('cartError');
    }
  }

  async fetchWithTimeout(url, options = {}, timeoutMs = 12000) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal,
      });
    } catch (error) {
      if (error?.name === 'AbortError') {
        const timeoutError = new Error('Die Anfrage hat zu lange gedauert.');
        timeoutError.code = 'REQUEST_TIMEOUT';
        throw timeoutError;
      }

      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  showInlineStatus(type, message, options = {}) {
    const existing = this.result?.querySelector('.fa-kc__inline-status');
    existing?.remove();

    const status = document.createElement('div');
    status.className = `fa-kc__inline-status fa-kc__inline-status--${type}`;
    const text = document.createElement('span');
    text.textContent = message;
    status.append(text);

    if (options.actions) {
      const actions = document.createElement('span');
      actions.className = 'fa-kc__inline-actions';

      const chooseMore = document.createElement('button');
      chooseMore.type = 'button';
      chooseMore.textContent = this.t('chooseAnother');
      chooseMore.addEventListener('click', () => {
        status.remove();
        const inputPanel = this.querySelector('.fa-kc__panel--input');
        inputPanel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.lengthNumber?.focus?.();
      });

      const checkout = document.createElement('a');
      checkout.href = '/checkout';
      checkout.textContent = this.t('checkoutGo');

      const cart = document.createElement('a');
      cart.href = this.config.cartUrl || '/cart';
      cart.textContent = this.t('cartGo');

      actions.append(chooseMore, checkout, cart);
      status.append(actions);
    }

    const target = options.target instanceof HTMLElement ? options.target : null;
    if (target) {
      target.append(status);
      return;
    }

    this.result?.prepend(status);
  }

  formatMoney(cents) {
    const currency = String(this.config.currency || 'CHF').toUpperCase();
    const amount = Number(cents || 0) / 100;
    const sign = amount < 0 ? '-' : '';
    const [whole, decimals] = Math.abs(amount).toFixed(2).split('.');
    return `${currency} ${sign}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${decimals}`;
  }

  localeTag() {
    return {
      de: 'de-CH',
      en: 'en-CH',
      fr: 'fr-CH',
      it: 'it-CH',
    }[this.locale] || 'de-CH';
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }
}

if (!customElements.get('keilrahmen-configurator')) {
  customElements.define('keilrahmen-configurator', KeilrahmenConfigurator);
}
